import React, {
  useEffect, useState, useMemo, Fragment, useCallback, useRef
} from "react";
import * as XLSX from 'xlsx';
import { DateRange } from 'react-date-range';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';

import {
  Chart as ChartJS, ArcElement, Tooltip, Legend,
  CategoryScale, LinearScale, BarElement, Title,
  PointElement, LineElement,
} from "chart.js";
import { Bar, Line, Doughnut } from "react-chartjs-2";

import {
  Box, Typography, Grid, Paper, CircularProgress, Alert,
  FormControl, InputLabel, Select, MenuItem, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, TableSortLabel,
  Chip, TablePagination, IconButton, Button, TableFooter,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Tabs, Tab, TextField, InputAdornment,
  Tooltip as MuiTooltip, Divider, Badge,
  Drawer, useMediaQuery, useTheme, Stack,
} from "@mui/material";
import { styled, alpha, keyframes } from "@mui/material/styles";

import KeyboardArrowDownIcon  from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import SearchIcon             from '@mui/icons-material/Search';
import ClearIcon              from '@mui/icons-material/Clear';
import TrendingUpIcon         from '@mui/icons-material/TrendingUp';
import TrendingDownIcon       from '@mui/icons-material/TrendingDown';
import PeopleIcon             from '@mui/icons-material/People';
import InventoryIcon          from '@mui/icons-material/Inventory';
import MapIcon                from '@mui/icons-material/Map';
import LocalShippingIcon      from '@mui/icons-material/LocalShipping';
import BarChartIcon           from '@mui/icons-material/BarChart';
import CalendarTodayIcon      from '@mui/icons-material/CalendarToday';
import FileDownloadIcon       from '@mui/icons-material/FileDownload';
import PrintIcon              from '@mui/icons-material/Print';
import EmojiEventsIcon        from '@mui/icons-material/EmojiEvents';
import ArrowUpwardIcon        from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon      from '@mui/icons-material/ArrowDownward';
import RemoveIcon             from '@mui/icons-material/Remove';
import CloseIcon              from '@mui/icons-material/Close';
import OpenInNewIcon          from '@mui/icons-material/OpenInNew';
import MenuIcon               from '@mui/icons-material/Menu';
import SearchOffIcon          from '@mui/icons-material/SearchOff';
import ShowChartIcon          from '@mui/icons-material/ShowChart';
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import BusinessIcon from '@mui/icons-material/Business';
import StorefrontIcon from '@mui/icons-material/Storefront';
import { useMsal }          from '@azure/msal-react';
import { getApiToken }      from '../authUtils';
import { backend_api_base_url } from '../config/urlconfig';
import { fetchAnalyticsOrders } from './dashboardApi';

ChartJS.register(
  ArcElement, Tooltip, Legend, CategoryScale,
  LinearScale, BarElement, Title, PointElement, LineElement
);

// ─── Helpers ────────────────────────────────────────────────────────────────────
const QUARTERS = ['Q1', 'Q2', 'Q3', 'Q4'];

const getQuarter = (dateStr) => {
  const m = new Date(dateStr).getMonth();
  if (m >= 3 && m <= 5) return 'Q1';
  if (m >= 6 && m <= 8) return 'Q2';
  if (m >= 9 && m <= 11) return 'Q3';
  return 'Q4';
};
const getFinancialYear = (dateStr) => {
  const d = new Date(dateStr);
  return d.getMonth() < 3 ? d.getFullYear() : d.getFullYear() + 1;
};
const getMonthYear = (dateStr) =>
  new Date(dateStr).toLocaleString('default', { month: 'short', year: 'numeric' });
const fmt = (v, pct = false) => {
  if (typeof v !== 'number' || isNaN(v)) return pct ? '0.00%' : '0.00';
  const s = new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v);
  return pct ? `${s}%` : s;
};
const fmtCur = (v) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(v || 0);
const fmtShort = (v) => {
  const n = v || 0;
  if (Math.abs(n) >= 10000000) return `₹${(n / 10000000).toFixed(1)}Cr`;
  if (Math.abs(n) >= 100000)   return `₹${(n / 100000).toFixed(1)}L`;
  if (Math.abs(n) >= 1000)     return `₹${(n / 1000).toFixed(1)}K`;
  return `₹${n.toFixed(0)}`;
};
const deltaColor = (v) => (v > 0 ? '#16a34a' : v < 0 ? '#dc2626' : '#6b7280');
const deltaIcon  = (v) =>
  v > 0 ? <ArrowUpwardIcon sx={{ fontSize: 11 }} />
  : v < 0 ? <ArrowDownwardIcon sx={{ fontSize: 11 }} />
  : <RemoveIcon sx={{ fontSize: 11 }} />;

const resolveSpocInfo = (entry, usersMap) => {
  const key = (entry.salesPOC || '').toLowerCase().trim();
  const u = usersMap[key];

  const serverName = entry.salesPOC || 'Unknown Employee';
  const serverCity = entry.salesPocCity || 'Unknown City';
  const serverState = entry.salesPocState || 'Unknown State';

  const correctedName = (u?.displayName && u.displayName.trim()) || null;
  const correctedCity = (u?.city && u.city.trim()) || null;
  const correctedState = (u?.state && u.state.trim()) || null;

  const nameCorrected = !!(correctedName && correctedName !== serverName);
  const cityCorrected = !!(correctedCity && correctedCity !== serverCity);
  const stateCorrected = !!(correctedState && correctedState !== serverState);

  return {
    salesSpoc: correctedName || serverName,
    salesPocCity: correctedCity || serverCity,
    salesPocState: correctedState || serverState,
    nameCorrected, cityCorrected, stateCorrected,
    anyCorrected: nameCorrected || cityCorrected || stateCorrected,
  };
};

    const CorrectedBadge = () => (
      <MuiTooltip title="Manually corrected in User Management System by admin. Backend data may differ." placement="top" arrow>
        <Chip size="small" label="Corrected" sx={{ height: 18, fontSize: '0.65rem', fontWeight: 700, bgcolor: '#fef3c7', color: '#92400e', border: '1px solid #fde68a', ml: 0.5, cursor: 'help' }} />
      </MuiTooltip>
    );

  //customRows Helper
  const getCustomTotal = (rows, key) =>
  rows?.reduce((sum, r) => sum + (parseFloat(r[key]) || 0), 0) || 0;


// ─── Animations ─────────────────────────────────────────────────────────────────
const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to   { opacity: 1; transform: none; }
`;

// ─── Styled ──────────────────────────────────────────────────────────────────────
const DashShell = styled(Box)({
  fontFamily: "'Sora','Nunito','system-ui',sans-serif",
  background: '#f8f9fc',
  minHeight: '100vh',
});
const StickyFilter = styled(Paper)(({ theme }) => ({
  position: 'sticky', top: 0, zIndex: 200,
  borderRadius: 0, borderBottom: `1px solid ${theme.palette.divider}`,
  padding: theme.spacing(1.5, 3),
  background: 'rgba(255,255,255,0.96)',
  backdropFilter: 'blur(8px)',
  boxShadow: '0 1px 8px rgba(0,0,0,0.06)',
}));
const KpiCard = styled(Paper)(({ theme }) => ({
  borderRadius: 18, padding: theme.spacing(2.5),
  boxShadow: '0 2px 16px rgba(0,0,0,0.06)', background: '#fff',
  position: 'relative', overflow: 'hidden',
  transition: 'transform .18s, box-shadow .18s',
  animation: `${fadeUp} .4s ease both`,
  '&:hover': { transform: 'translateY(-3px)', boxShadow: '0 8px 28px rgba(0,0,0,0.11)' },
}));
const ChartPaper = styled(Paper)(({ theme }) => ({
  borderRadius: 18, padding: theme.spacing(2.5),
  boxShadow: '0 2px 16px rgba(0,0,0,0.06)', background: '#fff',
  animation: `${fadeUp} .5s ease both`,
}));
const SectionWrap = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  [theme.breakpoints.down('sm')]: { padding: theme.spacing(2) },
}));
const StyledTab = styled(Tab)(() => ({
  fontWeight: 700, fontSize: '0.83rem', textTransform: 'none',
  letterSpacing: '0.01em', minHeight: 48,
  '&.Mui-selected': { color: '#1d4ed8' },
}));
const GainerCard = styled(Paper)(({ positive }) => ({
  borderRadius: 14, padding: 16,
  border: `1.5px solid ${positive ? '#bbf7d0' : '#fecaca'}`,
  background: positive ? '#f0fdf4' : '#fff1f2',
  display: 'flex', flexDirection: 'column', gap: 4,
  transition: 'transform .15s',
  '&:hover': { transform: 'scale(1.01)' },
}));

// ─── Atoms ───────────────────────────────────────────────────────────────────────
const DeltaBadge = ({ value }) => (
  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.3, color: deltaColor(value), fontWeight: 700, fontSize: '0.75rem' }}>
    {deltaIcon(value)}{Math.abs(value).toFixed(1)}%
  </Box>
);
const MarginChip = ({ value }) => (
  <Chip size="small"
    icon={value >= 0 ? <TrendingUpIcon fontSize="small" /> : <TrendingDownIcon fontSize="small" />}
    label={fmt(value, true)}
    sx={{ bgcolor: value >= 0 ? '#f0fdf4' : '#fff1f2', color: value >= 0 ? '#15803d' : '#dc2626', fontWeight: 700, fontSize: '0.74rem', border: `1px solid ${value >= 0 ? '#bbf7d0' : '#fecaca'}` }}
  />
);
const NoData = ({ msg = 'No data matches the current filters.' }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 6, bgcolor: '#f8fafc', borderRadius: 3, border: '1px dashed #cbd5e1', color: '#64748b' }}>
    <SearchOffIcon sx={{ fontSize: 48, color: '#94a3b8', mb: 2 }} />
    <Typography variant="subtitle1" fontWeight={700} color="text.secondary">No records found</Typography>
    <Typography variant="body2" color="text.disabled">{msg}</Typography>
  </Box>
);

const SummaryStrip = ({ totals, extra = [] }) => {
  const items = [
    { label: 'Total Sales',  value: fmtShort(totals.totalSp),  bg: '#eff6ff', color: '#1d4ed8' },
    { label: 'Total Cost',   value: fmtShort(totals.totalPp),  bg: '#faf5ff', color: '#7c3aed' },
    { label: 'Total Margin', value: fmtShort(totals.margin),   bg: totals.margin >= 0 ? '#f0fdf4' : '#fff1f2', color: totals.margin >= 0 ? '#15803d' : '#dc2626' },
    { label: 'Margin %',     value: fmt(totals.totalSp > 0 ? (totals.margin / totals.totalSp) * 100 : 0, true), bg: '#f0fdfa', color: '#0f766e' },
    ...extra,
  ];
  return (
    <Box sx={{ display: 'flex', gap: 2, px: 3, pb: 2, flexWrap: 'wrap' }}>
      {items.map(s => (
        <Box key={s.label} sx={{ bgcolor: s.bg, borderRadius: 2, px: 2, py: 1, minWidth: 110 }}>
          <Typography variant="caption" color="text.secondary" fontWeight={600}>{s.label}</Typography>
          <Typography fontWeight={800} sx={{ color: s.color, fontSize: '1rem' }}>{s.value}</Typography>
        </Box>
      ))}
    </Box>
  );
};

// ─── Data processing ─────────────────────────────────────────────────────────────
const processRaw = (allRawData, selectedYear, selectedQuarter, dateRange, usersMap = {}) => {
  if (!allRawData?.length) return { flat: [], product: [] };
  const { startDate, endDate } = dateRange[0];
  const flat = [], product = [];
  allRawData.forEach(entry => {
    if (!entry.date || !Array.isArray(entry.items) || !entry.items.length) return;
    const itemDate = new Date(entry.date);
    if (startDate && endDate) {
      const t = itemDate.getTime();
      const adj = new Date(endDate); adj.setHours(23, 59, 59, 999);
      if (t < startDate.getTime() || t > adj.getTime()) return;
    }
    const fy = getFinancialYear(entry.date);
    const q  = getQuarter(entry.date);
    if (selectedYear !== 'all' && fy !== parseInt(selectedYear, 10)) return;
    if (selectedQuarter !== 'all' && q !== selectedQuarter) return;
    const subSp = entry.items.reduce((s, i) => s + (Number(i.sp)||0) * (Number(i.qty)||0), 0);
    const subPp = entry.items.reduce((s, i) => s + (Number(i.pp)||0) * (Number(i.qty)||0), 0);

    const customSp = getCustomTotal(entry.customRows, 'customTotalSp');
    const customPp = getCustomTotal(entry.customRows, 'customTotalPp');

    // Mirror salesInvoiceDashboard exactly — use entry directly, not entry.items[0]
    const tSp = subSp + customSp + (Number(entry.transportationTotalSp) || 0) + (Number(entry.customizationTotalSp) || 0) + (Number(entry.marketingTotalSp) || 0);
    const tPp = subPp + customPp + (Number(entry.transportationTotalPp) || 0) + (Number(entry.customizationTotalPp) || 0) + (Number(entry.marketingTotalPp) || 0);

    const margin = tSp - tPp;
    const spocInfo = resolveSpocInfo(entry, usersMap);
    flat.push({
      clientName: entry.clientName, costsheetfor: entry.costsheetfor,
      date: entry.date, quarter: q, monthYear: getMonthYear(entry.date), year: fy,
      salesOrderNo: entry.salesOrderNo,
      vendorName: [...new Set(entry.items.map(i => i.vendorName?.trim()).filter(Boolean))].join(', '), // <-- FIXED HERE
      // salesSpoc: entry.salesPOC, salesPocState: entry.salesPocState, salesPocCity: entry.salesPocCity,
      // salesSpoc:    (() => { const k = (entry.salesPOC||'').toLowerCase().trim(); const u = usersMap[k]; return u?.displayName || entry.salesPOC || 'Unknown Employee'; })(),
      // salesPocCity: (() => { const k = (entry.salesPOC||'').toLowerCase().trim(); const u = usersMap[k]; return (u?.city && u.city.trim()) || entry.salesPocCity || 'Unknown City'; })(),
      // salesPocState:(() => { const k = (entry.salesPOC||'').toLowerCase().trim(); const u = usersMap[k]; return (u?.state && u.state.trim()) || entry.salesPocState || 'Unknown State'; })(),
      salesSpoc: spocInfo.salesSpoc,
      salesPocCity: spocInfo.salesPocCity,
      salesPocState: spocInfo.salesPocState,
      spocAnyCorrected: spocInfo.anyCorrected,
      totalSp: tSp, totalPp: tPp, margin, marginPercentage: tSp > 0 ? (margin / tSp) * 100 : 0,
      customSpTotal:Number(entry.customSp) || 0, customPpTotal:Number(entry.customPp) || 0,
      internalCategory: entry.internalCategory || 'Unknown Internal Category',
    });
    entry.items.forEach(sub => {
      //Skip proudcts where cost sheet for equals to internal
        if ((entry.costsheetfor || '').toLowerCase() === 'internal') return;
      const sp = Number(sub.sp)||0, gst = Number(sub.gst)||0, qty = Number(sub.qty)||0;
      const iSp = sp*qty, iPp = (Number(sub.pp)||0)*qty;

      const margin = iSp - iPp;
      product.push({
          salesOrderNo: entry.salesOrderNo,
          costsheetfor: entry.costsheetfor,
          clientName:   entry.clientName,
          date:         entry.date,
          quarter:      q,
          year:         fy,
          qty,
          totalSp:      iSp,
          totalPp:      iPp,
          margin:       margin,
          marginPercentage: iPp > 0 ? (margin / iSp) * 100 : 0,
          brand:       sub.brand?.name       || 'Unknown Brand',
          category:    sub.category?.name    || 'Unknown Category',
          subcategory: sub.subcategory?.name || 'Unknown Subcategory',
          internalCategory: entry.internalCategory || "Unknown Internal Category",

      });
    });
  });
  return { flat, product };
};

const calcSummary = (flatData) => {
  const s = flatData.reduce(
    (a, d) => { a.totalSp += d.totalSp; a.totalPp += d.totalPp; a.totalMargin += d.margin||0; a.customSpTotal += d.customSpTotal||0; a.customPpTotal += d.customPpTotal||0; a.clients.add(d.clientName); return a; },
{ totalSp: 0, totalPp: 0, totalMargin: 0, customSpTotal: 0, customPpTotal: 0, clients: new Set() }
  );
  s.clientCount = s.clients.size;
  // s.avgMargin   = s.totalSp > 0 ? (s.totalMargin / s.totalSp) * 100 : 0;
  s.avgMargin = s.totalPp > 0 ? (s.totalMargin / s.totalPp) * 100 : 0;
  return s;
};

// ─── Period comparison table (QoQ for a single entity) ───────────────────────────
const PeriodCompareTable = ({ records, groupBy = 'quarter' }) => {
  const byQ = useMemo(() => {
    const g = {};
    QUARTERS.forEach(q => { g[q] = { totalSp: 0, totalPp: 0, margin: 0, count: 0 }; });
    records.forEach(r => {
      const q = r.quarter;
      if (!g[q]) return;
      g[q].totalSp += r.totalSp; g[q].totalPp += r.totalPp; g[q].margin += r.margin; g[q].count++;
    });
    QUARTERS.forEach(q => {
      // g[q].marginPct = g[q].totalSp > 0 ? (g[q].margin / g[q].totalSp) * 100 : 0;
      g[q].marginPct = g[q].totalPp > 0 ? (g[q].margin / g[q].totalPp) * 100 : 0;
    });
    return g;
  }, [records]);

  const th = { fontWeight: 700, bgcolor: '#f8fafc', fontSize: '0.78rem', textAlign: 'center' };

  const deltaVsPrev = (q, key) => {
    const idx = QUARTERS.indexOf(q);
    if (idx === 0) return null;
    const prev = byQ[QUARTERS[idx - 1]][key];
    const cur  = byQ[q][key];
    return prev !== 0 ? ((cur - prev) / Math.abs(prev)) * 100 : null;
  };

  return (
    <Box sx={{ mt: 1 }}>
      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <ShowChartIcon fontSize="small" sx={{ color: '#1d4ed8' }} /> Quarter-on-Quarter Breakdown
      </Typography>
      <TableContainer sx={{ borderRadius: 2, border: '1px solid #e2e8f0' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...th, textAlign: 'left' }}>Metric</TableCell>
              {QUARTERS.map(q => <TableCell key={q} sx={th}>{q}</TableCell>)}
            </TableRow>
          </TableHead>
          <TableBody>
            {[
              { label: 'Sales',     key: 'totalSp',   render: fmtShort },
              { label: 'Cost',      key: 'totalPp',   render: fmtShort },
              { label: 'Margin',    key: 'margin',    render: fmtShort },
              { label: 'Margin %',  key: 'marginPct', render: v => fmt(v, true) },
              { label: 'Orders',    key: 'count',     render: v => v },
            ].map(row => (
              <TableRow key={row.label} hover>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem' }}>{row.label}</TableCell>
                {QUARTERS.map(q => {
                  const val = byQ[q][row.key];
                  const d   = deltaVsPrev(q, row.key);
                  const active = byQ[q].count > 0;
                  return (
                    <TableCell key={q} align="center" sx={{ fontSize: '0.8rem', color: active ? 'inherit' : '#cbd5e1' }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.2 }}>
                        <span style={{ fontWeight: active ? 700 : 400 }}>{active ? row.render(val) : '—'}</span>
                        {d !== null && active && <DeltaBadge value={d} />}
                      </Box>
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

// ─── Rich costsheet drill-down dialog ────────────────────────────────────────────
const CostsheetDrillDialog = ({ open, onClose, title, subtitle, records, showQoQ = true }) => {
  const [tab, setTab] = useState(0); // 0 = Costsheets, 1 = QoQ
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rpp, setRpp] = useState(15);
  const [sortBy, setSortBy] = useState(null);
  const [sortDir, setSortDir] = useState('desc');

  const totals = useMemo(() => records.reduce(
    (a, r) => { a.totalSp += r.totalSp; a.totalPp += r.totalPp; a.margin += r.margin; return a; },
    { totalSp: 0, totalPp: 0, margin: 0 }
  ), [records]);

  const filtered = useMemo(() =>{
    let data =
    !search ? records : records.filter(r =>
      (r.clientName||'').toLowerCase().includes(search.toLowerCase()) ||
      (r.salesOrderNo||'').toLowerCase().includes(search.toLowerCase()) ||
      (r.costsheetfor||'').toLowerCase().includes(search.toLowerCase())
      );
    if (sortBy) {
      data = [...data].sort((a, b) =>
        sortDir === 'asc'
          ? a[sortBy] - b[sortBy]
          : b[sortBy] - a[sortBy]
      );
    }

    return data;
  }, [records, search, sortBy, sortDir]);

  const exportXlsx = () => {
    const rows = filtered.map(r => ({
      'Sales Order No':    r.salesOrderNo || '',
      'Client':            r.clientName,
      'Date':              r.date,
      'Quarter':           r.quarter,
      'Cost Sheet For':    r.costsheetfor || '',
      'Sales (INR)':       r.totalSp,
      'Cost (INR)':        r.totalPp,
      'Margin (INR)':      r.margin,
      'Margin %':          fmt(r.marginPercentage, true),
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    ws['!cols'] = Object.keys(rows[0] || {}).map(k => ({ wch: Math.max(k.length, 14) }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Costsheets');
    XLSX.writeFile(wb, `${(title || 'export').replace(/\s+/g, '_')}.xlsx`);
  };

  const th = { fontWeight: 700, bgcolor: '#f8fafc', fontSize: '0.79rem', whiteSpace: 'nowrap' };
  const paginated = filtered.slice(page * rpp, page * rpp + rpp);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth
      PaperProps={{ sx: { borderRadius: 3, maxHeight: '90vh' } }}>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', fontWeight: 800, pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <OpenInNewIcon fontSize="small" sx={{ color: '#1d4ed8' }} />
          <Box>
            <Typography fontWeight={800} variant="h6" sx={{ lineHeight: 1.2 }}>{title}</Typography>
            {subtitle && <Typography variant="caption" color="text.secondary">{subtitle}</Typography>}
          </Box>
        </Box>
        <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
      </DialogTitle>

      <SummaryStrip totals={totals} extra={[{ label: 'Cost Sheets', value: records.length, bg: '#f1f5f9', color: '#334155' }]} />

      {showQoQ && (
        <Box sx={{ px: 3, mb: 1 }}>
          <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ minHeight: 36 }}>
            <Tab label="Cost Sheets" sx={{ textTransform: 'none', fontWeight: 700, fontSize: '0.82rem', minHeight: 36 }} />
            <Tab label="QoQ Comparison" sx={{ textTransform: 'none', fontWeight: 700, fontSize: '0.82rem', minHeight: 36 }} />
          </Tabs>
          <Divider />
        </Box>
      )}

      <DialogContent sx={{ p: 0, px: showQoQ && tab === 1 ? 3 : 0, pb: 1 }}>
        {(!showQoQ || tab === 0) ? (
          <>
            <Box sx={{ px: 3, py: 1.5, display: 'flex', gap: 2, alignItems: 'center', borderBottom: '1px solid #f1f5f9' }}>
              <TextField size="small" placeholder="Search client, order no, type…" value={search}
                onChange={e => { setSearch(e.target.value); setPage(0); }}
                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
                sx={{ width: 300 }}
              />
              <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                {filtered.length} of {records.length} records
              </Typography>
            </Box>
            <TableContainer sx={{ maxHeight: 400 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow sx={{ '& th': th }}>
                    <TableCell>#</TableCell>
                    <TableCell>Sales Order No</TableCell>
                    <TableCell>Client</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Qtr</TableCell>
                    <TableCell>Cost Sheet For</TableCell>
                    <TableCell align="right" sx={{ cursor: 'pointer' }}
                    onClick={() => { setSortBy('totalSp'); setSortDir(d => sortBy==='totalSp' && d==='desc' ? 'asc' : 'desc'); }}
                    >Sales {sortBy === 'totalSp' ? (sortDir === 'asc' ? '▲' : '▼') : ''}</TableCell>
                    <TableCell align="right" sx={{ cursor: 'pointer' }}
                    onClick={() => { setSortBy('totalPp'); setSortDir(d => sortBy==='totalPp' && d==='desc' ? 'asc' : 'desc'); }}
                    >Cost {sortBy === 'totalPp' ? (sortDir === 'asc' ? '▲' : '▼') : ''}</TableCell>
                    <TableCell align="right" sx={{ cursor: 'pointer' }}
                    onClick={() => { setSortBy('margin'); setSortDir(d => sortBy==='margin' && d==='desc' ? 'asc' : 'desc'); }}
                    >Margin {sortBy === 'margin' ? (sortDir === 'asc' ? '▲' : '▼') : ''}</TableCell>
                    <TableCell align="right" sx={{ cursor: 'pointer' }}
                    onClick={() => { setSortBy('marginPercentage'); setSortDir(d => sortBy==='marginPercentage' && d==='desc' ? 'asc' : 'desc'); }}
                    >Margin % {sortBy === 'marginPercentage' ? (sortDir === 'asc' ? '▲' : '▼') : ''}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginated.map((r, i) => (
                    <TableRow key={i} hover>
                      <TableCell sx={{ color: '#94a3b8', fontSize: '0.78rem' }}>{page * rpp + i + 1}</TableCell>
                      <TableCell sx={{ fontSize: '0.82rem', fontWeight: 600 }}>{r.salesOrderNo || '—'}</TableCell>
                      <TableCell sx={{ fontSize: '0.82rem', fontWeight: 600 }}>{r.clientName}</TableCell>
                      <TableCell sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>{r.date?.split('T')[0] || r.date}</TableCell>
                      <TableCell><Chip size="small" label={r.quarter} sx={{ height: 18, fontSize: '0.68rem' }} /></TableCell>
                      <TableCell sx={{ fontSize: '0.82rem' }}>{r.costsheetfor || '—'}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>{fmtShort(r.totalSp)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>{fmtShort(r.totalPp)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: r.margin >= 0 ? '#15803d' : '#dc2626' }}>{fmtShort(r.margin)}</TableCell>
                      <TableCell align="right"><MarginChip value={r.marginPercentage} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination component="div" count={filtered.length} page={page}
              onPageChange={(_, p) => setPage(p)} rowsPerPage={rpp}
              onRowsPerPageChange={e => { setRpp(parseInt(e.target.value, 10)); setPage(0); }}
              rowsPerPageOptions={[15, 25, 50, 100]}
            />
          </>
        ) : (
          <PeriodCompareTable records={records} />
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #e2e8f0' }}>
        <Button variant="outlined" startIcon={<FileDownloadIcon />} onClick={exportXlsx}>
          Export to Excel
        </Button>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── KPI Row ─────────────────────────────────────────────────────────────────────
const KpiRow = ({ current, previous }) => {
  const delta = (cur, prev) => (prev > 0 ? ((cur - prev) / prev) * 100 : 0);
  const cards = [
    {label: 'Total Sales', value: fmtShort(current.totalSp), full: `${fmtCur(current.totalSp)} Excl. Inventory`,   d: delta(current.totalSp,previous?.totalSp),    icon: '💰', bg: 'linear-gradient(135deg,#1d4ed8,#3b82f6)' },
    { label: 'Total Cost', value: fmtShort(current.totalPp), full: `${fmtCur(current.totalPp)} Excl. Inventory`,   d: delta(current.totalPp,previous?.totalPp),    icon: '🛒', bg: 'linear-gradient(135deg,#7c3aed,#a78bfa)' },
    { label: 'Total Margin',   value: fmtShort(current.totalMargin),full: fmtCur(current.totalMargin),d: delta(current.totalMargin,previous?.totalMargin),icon: current.totalMargin>=0?'📈':'📉', bg: current.totalMargin>=0?'linear-gradient(135deg,#15803d,#4ade80)':'linear-gradient(135deg,#dc2626,#f87171)' },
    { label: 'Avg Margin %',   value: fmt(current.avgMargin,true),  full: 'Blended margin',           d: delta(current.avgMargin,previous?.avgMargin),  icon: '🎯', bg: 'linear-gradient(135deg,#0f766e,#2dd4bf)' },
    { label: 'Unique Clients', value: current.clientCount,          full: 'Active in period',         d: delta(current.clientCount,previous?.clientCount),icon: '🏢', bg: 'linear-gradient(135deg,#c2410c,#fb923c)' },
  ];
  return (
    <Grid container spacing={2} sx={{ mb: 3 }}>
      {cards.map((c, i) => (
        <Grid item xs={6} sm={4} sx={{ width: { md: '20%' }, maxWidth: { md: '20%' } }} key={c.label} className="print-kpi">
          <KpiCard sx={{ animationDelay: `${i * 60}ms` }}>
            <Box sx={{ position: 'absolute', right: -12, top: -12, width: 72, height: 72, borderRadius: '50%', background: c.bg, opacity: 0.12 }} />
            <Typography sx={{ fontSize: 26, lineHeight: 1, mb: 0.5 }}>{c.icon}</Typography>
            <MuiTooltip title={c.full} placement="top">
              <Typography variant="h5" fontWeight={800} sx={{ lineHeight: 1.1, letterSpacing: '-0.5px' }}>{c.value}</Typography>
            </MuiTooltip>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
              <Typography variant="caption" color="text.secondary" fontWeight={600}>{c.label}</Typography>
              {previous && <DeltaBadge value={c.d} />}
            </Box>
            {previous && <Typography variant="caption" sx={{ color: '#9ca3af', fontSize: '0.68rem' }}>vs prev period</Typography>}
          </KpiCard>
        </Grid>
      ))}
    </Grid>
  );
};

// ─── Gainers & Losers ────────────────────────────────────────────────────────────
const GainersLosers = ({ current, previous }) => {
  const { gainers, losers } = useMemo(() => {
    if (!previous?.length) return { gainers: [], losers: [] };
    const prevMap = {}, curMap = {};
    previous.forEach(d => { prevMap[d.clientName] = (prevMap[d.clientName] || 0) + d.margin; });
    current.forEach(d  => { curMap[d.clientName]  = (curMap[d.clientName]  || 0) + d.margin; });
    const changes = Object.entries(curMap).map(([name, cur]) => {
      const prev = prevMap[name] || 0, delta = prev !== 0 ? ((cur - prev) / Math.abs(prev)) * 100 : 0;
      return { name, cur, prev, delta, abs: cur - prev };
    }).filter(x => x.prev !== 0 || x.cur !== 0);
    const sorted = [...changes].sort((a, b) => b.delta - a.delta);
    return { gainers: sorted.slice(0, 3), losers: sorted.slice(-3).reverse() };
  }, [current, previous]);

  if (!previous || (!gainers.length && !losers.length)) return null;
  return (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <EmojiEventsIcon sx={{ color: '#d97706' }} />
        <Typography variant="h6" fontWeight={800}>Top Movers</Typography>
        <Chip size="small" label="vs prev period" sx={{ bgcolor: '#f1f5f9', fontSize: '0.72rem' }} />
      </Box>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6} className="print-col-6">
          <Typography variant="subtitle2" fontWeight={700} color="success.dark" sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <ArrowUpwardIcon fontSize="small" /> Top Gainers (Margin)
          </Typography>
          <Stack spacing={1.5}>
            {gainers.map(g => (
              <GainerCard positive={1} key={g.name} elevation={0}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Typography variant="body2" fontWeight={700}>{g.name}</Typography>
                  <DeltaBadge value={g.delta} />
                </Box>
                <Typography variant="caption" color="text.secondary">
                  {fmtShort(g.prev)} → <strong>{fmtShort(g.cur)}</strong> (+{fmtShort(g.abs)})
                </Typography>
              </GainerCard>
            ))}
          </Stack>
        </Grid>
        <Grid item xs={12} md={6} className="print-col-6">
          <Typography variant="subtitle2" fontWeight={700} color="error.dark" sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <ArrowDownwardIcon fontSize="small" /> Top Losers (Margin)
          </Typography>
          <Stack spacing={1.5}>
            {losers.map(g => (
              <GainerCard positive={0} key={g.name} elevation={0}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Typography variant="body2" fontWeight={700}>{g.name}</Typography>
                  <DeltaBadge value={g.delta} />
                </Box>
                <Typography variant="caption" color="text.secondary">
                  {fmtShort(g.prev)} → <strong>{fmtShort(g.cur)}</strong> ({fmtShort(g.abs)})
                </Typography>
              </GainerCard>
            ))}
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
};

// ─── Drillable chart — clicking a bar opens the rich CostsheetDrillDialog ────────
const DrillableChart = ({ title, subtitle, ChartComp, data, options, flatData, matchFn, height = 280 }) => {
  const [drill, setDrill] = useState(null); // label string

  const defaultMatch = useCallback((r, label) =>
    r.clientName === label || r.salesSpoc === label || r.category === label || r.brand === label,
  []);

  const drillRecords = useMemo(() => {
    if (!drill || !flatData) return [];
    const fn = matchFn || defaultMatch;
    return flatData.filter(r => fn(r, drill));
  }, [drill, flatData, matchFn, defaultMatch]);

  const clickOpts = useMemo(() => ({
    ...options,
    onHover: (event, chartElement) => {
      event.native.target.style.cursor = chartElement[0] ? 'pointer' : 'default';
    },
    onClick: (_, elements) => {
      if (elements.length > 0) setDrill(data.labels[elements[0].index]);
    },
    plugins: {
      ...options.plugins,
      tooltip: {
        ...options.plugins?.tooltip,
        callbacks: {
          ...options.plugins?.tooltip?.callbacks,
          afterLabel: () => 'Click to drill down →',
        },
      },
    },
  }), [options, data]);

  return (
    <>
      <ChartPaper>
        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.3 }}>{title}</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
          {subtitle || 'Click any bar to see cost sheets & QoQ breakdown'}
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <Box sx={{ height, position: 'relative', width: '100%', overflow: 'hidden' }} className="chart-wrapper">
          <ChartComp data={data} options={clickOpts} />
        </Box>
      </ChartPaper>
      <CostsheetDrillDialog
        open={!!drill}
        onClose={() => setDrill(null)}
        title={drill || ''}
        subtitle={`${drillRecords.length} cost sheets`}
        records={drillRecords}
        showQoQ
      />
    </>
  );
};

// ─── Legal Client pivot table — groups by mapped legal name, falls back to raw name ──
const LegalClientPivotTable = ({ data, rawToLegalMap }) => {
  const [orderBy, setOrderBy] = useState('totalSp');
  const [order, setOrder] = useState('desc');
  const [page, setPage] = useState(0);
  const [rpp, setRpp] = useState(10);
  const [metric, setMetric] = useState('totalSp');
  const [search, setSearch] = useState('');
  const [filterMapped, setFilterMapped] = useState('all'); // 'all' | 'mapped' | 'unmapped'
  const [drill, setDrill] = useState(null);

  const { rows, summary } = useMemo(() => {
    const g = {};
    data.forEach(item => {
      const legalName = rawToLegalMap[item.clientName];
      const k = legalName || item.clientName;
      const isMapped = !!legalName;
      if (!g[k]) {
        g[k] = { clientName: k, isMapped, totalSp: 0, totalPp: 0, totalMargin: 0, recordCount: 0, _records: [] };
        QUARTERS.forEach(q => { g[k][q] = { totalSp: 0, totalPp: 0, margin: 0 }; });
      }
      const c = g[k], q = item.quarter;
      c[q].totalSp += item.totalSp; c[q].totalPp += item.totalPp; c[q].margin += item.margin;
      c.totalSp += item.totalSp; c.totalPp += item.totalPp; c.totalMargin += item.margin; c.recordCount++;
      c._records.push(item);
    });
    const grand = { totalSp: 0, totalPp: 0, totalMargin: 0, clientCount: 0 };
    QUARTERS.forEach(q => { grand[q] = { totalSp: 0, totalPp: 0, margin: 0 }; });
    Object.values(g).forEach(c => {
      c.marginPercentage = c.totalSp > 0 ? (c.totalMargin / c.totalSp) * 100 : 0;
      QUARTERS.forEach(q => {
        c[q].marginPercentage = c[q].totalSp > 0 ? (c[q].margin / c[q].totalSp) * 100 : 0;
        grand[q].totalSp += c[q].totalSp; grand[q].totalPp += c[q].totalPp; grand[q].margin += c[q].margin;
      });
      grand.totalSp += c.totalSp; grand.totalPp += c.totalPp; grand.totalMargin += c.totalMargin; grand.clientCount++;
    });
    grand.marginPercentage = grand.totalSp > 0 ? (grand.totalMargin / grand.totalSp) * 100 : 0;
    QUARTERS.forEach(q => { grand[q].marginPercentage = grand[q].totalSp > 0 ? (grand[q].margin / grand[q].totalSp) * 100 : 0; });
    return { rows: Object.values(g), summary: grand };
  }, [data, rawToLegalMap]);

  const sorted = useMemo(() => {
    return [...rows]
      .filter(r => !search || r.clientName.toLowerCase().includes(search.toLowerCase()))
      .filter(r => filterMapped === 'all' || (filterMapped === 'mapped' ? r.isMapped : !r.isMapped))
      .sort((a, b) => {
        if (orderBy === 'clientName') return order === 'asc' ? a.clientName.localeCompare(b.clientName) : b.clientName.localeCompare(a.clientName);
        const [q, m] = orderBy.includes('.') ? orderBy.split('.') : [null, orderBy];
        const va = q ? a[q][m] : a[m], vb = q ? b[q][m] : b[m];
        return order === 'asc' ? va - vb : vb - va;
      });
  }, [rows, order, orderBy, search, filterMapped]);

  const paginated = sorted.slice(page * rpp, page * rpp + rpp);
  const handleSort = (p) => { setOrder(orderBy === p && order === 'asc' ? 'desc' : 'asc'); setOrderBy(p); };

  const renderVal = (val, m) => {
    if (m === 'marginPercentage') return <MarginChip value={val} />;
    return <Typography variant="body2" fontWeight={600}>{fmtShort(val)}</Typography>;
  };

  const exportXlsx = () => {
    const r = sorted.map(row => ({
      'Legal Client': row.clientName, 'Mapped': row.isMapped ? 'Yes' : 'No',
      'Q1 Sales': row.Q1.totalSp, 'Q1 Margin': row.Q1.margin, 'Q1 Margin%': row.Q1.marginPercentage,
      'Q2 Sales': row.Q2.totalSp, 'Q2 Margin': row.Q2.margin, 'Q2 Margin%': row.Q2.marginPercentage,
      'Q3 Sales': row.Q3.totalSp, 'Q3 Margin': row.Q3.margin, 'Q3 Margin%': row.Q3.marginPercentage,
      'Q4 Sales': row.Q4.totalSp, 'Q4 Margin': row.Q4.margin, 'Q4 Margin%': row.Q4.marginPercentage,
      'Total Sales': row.totalSp, 'Total Margin': row.totalMargin, 'Margin%': row.marginPercentage,
    }));
    const ws = XLSX.utils.json_to_sheet(r); const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'LegalClients'); XLSX.writeFile(wb, 'legal_client_analytics.xlsx');
  };

  const th = { fontWeight: 700, bgcolor: '#f8fafc', fontSize: '0.79rem', whiteSpace: 'nowrap' };

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField size="small" placeholder="Search legal client…" value={search}
          onChange={e => { setSearch(e.target.value); setPage(0); }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          sx={{ width: 220 }}
        />
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Metric</InputLabel>
          <Select value={metric} label="Metric" onChange={e => setMetric(e.target.value)}>
            <MenuItem value="totalSp">Total Sales</MenuItem>
            <MenuItem value="totalMargin">Margin</MenuItem>
            <MenuItem value="marginPercentage">Margin %</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Mapping</InputLabel>
          <Select value={filterMapped} label="Mapping" onChange={e => { setFilterMapped(e.target.value); setPage(0); }}>
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="mapped">Mapped Only</MenuItem>
            <MenuItem value="unmapped">Unmapped Only</MenuItem>
          </Select>
        </FormControl>
        <Box sx={{ ml: 'auto' }}>
          <Button size="small" variant="outlined" startIcon={<FileDownloadIcon />} onClick={exportXlsx}>Export</Button>
        </Box>
      </Box>

      <TableContainer sx={{ maxHeight: 540, borderRadius: 2, border: '1px solid #e2e8f0' }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...th, minWidth: 200, position: 'sticky', left: 0, zIndex: 3 }}>
                <TableSortLabel active={orderBy === 'clientName'} direction={orderBy === 'clientName' ? order : 'asc'} onClick={() => handleSort('clientName')}>Legal Client</TableSortLabel>
              </TableCell>
              {QUARTERS.map(q => (
                <TableCell key={q} align="right" sx={th}>
                  <TableSortLabel active={orderBy === `${q}.${metric}`} direction={orderBy === `${q}.${metric}` ? order : 'asc'} onClick={() => handleSort(`${q}.${metric}`)}>{q}</TableSortLabel>
                </TableCell>
              ))}
              <TableCell align="right" sx={{ ...th, bgcolor: '#eff6ff' }}>
                <TableSortLabel active={orderBy === metric} direction={orderBy === metric ? order : 'asc'} onClick={() => handleSort(metric)}>Total</TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginated.map(row => (
              <TableRow key={row.clientName} hover
                onClick={() => setDrill({ name: row.clientName, records: row._records })}
                sx={{ cursor: 'pointer', '&:hover': { bgcolor: alpha('#0f766e', 0.03) } }}>
                <TableCell sx={{ position: 'sticky', left: 0, bgcolor: '#fff', zIndex: 1, minWidth: 200 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="body2" fontWeight={700}>{row.clientName}</Typography>
                    {!row.isMapped && (
                      <Chip size="small" label="Unmapped" sx={{ height: 18, fontSize: '0.65rem', bgcolor: '#fff7ed', color: '#c2410c' }} />
                    )}
                    <MuiTooltip title="View cost sheets & QoQ breakdown">
                      <OpenInNewIcon sx={{ fontSize: 13, color: '#94a3b8' }} />
                    </MuiTooltip>
                  </Box>
                  <Typography variant="caption" color="text.disabled">{row.recordCount} orders</Typography>
                </TableCell>
                {QUARTERS.map(q => (
                  <TableCell key={q} align="right">{renderVal(row[q][metric === 'totalMargin' ? 'margin' : metric], metric)}</TableCell>
                ))}
                <TableCell align="right" sx={{ bgcolor: '#f8fafc', fontWeight: 800 }}>{renderVal(row[metric], metric)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
          <TableFooter>
            <TableRow sx={{ '& td': { fontWeight: 800, borderTop: '2px solid #cbd5e1', bgcolor: '#f1f5f9' } }}>
              <TableCell>Grand Total</TableCell>
              {QUARTERS.map(q => (
                <TableCell key={q} align="right">{renderVal(summary[q][metric === 'totalMargin' ? 'margin' : metric], metric)}</TableCell>
              ))}
              <TableCell align="right">{renderVal(summary[metric], metric)}</TableCell>
            </TableRow>
          </TableFooter>
        </Table>
      </TableContainer>
      <TablePagination component="div" count={sorted.length} page={page} onPageChange={(_, p) => setPage(p)} rowsPerPage={rpp} onRowsPerPageChange={e => { setRpp(parseInt(e.target.value, 10)); setPage(0); }} rowsPerPageOptions={[10, 25, 50, 100]} />

      <CostsheetDrillDialog
        open={!!drill}
        onClose={() => setDrill(null)}
        title={drill?.name || ''}
        subtitle={`${drill?.records?.length || 0} cost sheets`}
        records={drill?.records || []}
        showQoQ
      />
    </Box>
  );
};

// ─── Client pivot table ──────────────────────────────────────────────────────────
const ClientPivotTable = ({ data }) => {
  const [orderBy, setOrderBy] = useState('totalSp');
  const [order,   setOrder]   = useState('desc');
  const [page,    setPage]    = useState(0);
  const [rpp,     setRpp]     = useState(10);
  const [metric,  setMetric]  = useState('totalSp');
  const [search,  setSearch]  = useState('');
  const [drill,   setDrill]   = useState(null);

  const { rows, summary } = useMemo(() => {
    const g = {};
    data.forEach(item => {
      const k = item.clientName;
      if (!g[k]) { g[k] = { clientName: k, totalSp: 0, totalPp: 0, totalMargin: 0, recordCount: 0, _records: [] }; QUARTERS.forEach(q => { g[k][q] = { totalSp: 0, totalPp: 0, margin: 0 }; }); }
      const c = g[k], q = item.quarter;
      c[q].totalSp += item.totalSp; c[q].totalPp += item.totalPp; c[q].margin += item.margin;
      c.totalSp += item.totalSp; c.totalPp += item.totalPp; c.totalMargin += item.margin; c.recordCount++;
      c._records.push(item);
    });
    const grand = { totalSp: 0, totalPp: 0, totalMargin: 0, clientCount: 0 };
    QUARTERS.forEach(q => { grand[q] = { totalSp: 0, totalPp: 0, margin: 0 }; });
    Object.values(g).forEach(c => {
      c.marginPercentage = c.totalSp > 0 ? (c.totalMargin / c.totalSp) * 100 : 0;
      QUARTERS.forEach(q => {
        c[q].marginPercentage = c[q].totalSp > 0 ? (c[q].margin / c[q].totalSp) * 100 : 0;
        grand[q].totalSp += c[q].totalSp; grand[q].totalPp += c[q].totalPp; grand[q].margin += c[q].margin;
      });
      grand.totalSp += c.totalSp; grand.totalPp += c.totalPp; grand.totalMargin += c.totalMargin; grand.clientCount++;
    });
    grand.marginPercentage = grand.totalSp > 0 ? (grand.totalMargin / grand.totalSp) * 100 : 0;
    QUARTERS.forEach(q => { grand[q].marginPercentage = grand[q].totalSp > 0 ? (grand[q].margin / grand[q].totalSp) * 100 : 0; });
    return { rows: Object.values(g), summary: grand };
  }, [data]);

  const sorted = useMemo(() => {
    return [...rows].filter(r => !search || r.clientName.toLowerCase().includes(search.toLowerCase())).sort((a, b) => {
      if (orderBy === 'clientName') return order === 'asc' ? a.clientName.localeCompare(b.clientName) : b.clientName.localeCompare(a.clientName);
      const [q, m] = orderBy.includes('.') ? orderBy.split('.') : [null, orderBy];
      const va = q ? a[q][m] : a[m], vb = q ? b[q][m] : b[m];
      return order === 'asc' ? va - vb : vb - va;
    });
  }, [rows, order, orderBy, search]);

  const paginated = sorted.slice(page * rpp, page * rpp + rpp);
  const handleSort = (p) => { setOrder(orderBy === p && order === 'asc' ? 'desc' : 'asc'); setOrderBy(p); };

  const renderVal = (val, m) => {
    if (m === 'marginPercentage') return <MarginChip value={val} />;
    return <Typography variant="body2" fontWeight={600}>{fmtShort(val)}</Typography>;
  };

  const exportXlsx = () => {
    const r = sorted.map(row => ({ Client: row.clientName, 'Q1 Sales': row.Q1.totalSp, 'Q1 Margin': row.Q1.margin, 'Q1 Margin%': row.Q1.marginPercentage, 'Q2 Sales': row.Q2.totalSp, 'Q2 Margin': row.Q2.margin, 'Q2 Margin%': row.Q2.marginPercentage, 'Q3 Sales': row.Q3.totalSp, 'Q3 Margin': row.Q3.margin, 'Q3 Margin%': row.Q3.marginPercentage, 'Q4 Sales': row.Q4.totalSp, 'Q4 Margin': row.Q4.margin, 'Q4 Margin%': row.Q4.marginPercentage, 'Total Sales': row.totalSp, 'Total Margin': row.totalMargin, 'Margin%': row.marginPercentage }));
    const ws = XLSX.utils.json_to_sheet(r); const wb = XLSX.utils.book_new(); XLSX.utils.book_append_sheet(wb, ws, 'Clients'); XLSX.writeFile(wb, 'client_analytics.xlsx');
  };

  const th = { fontWeight: 700, bgcolor: '#f8fafc', fontSize: '0.79rem', whiteSpace: 'nowrap' };

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField size="small" placeholder="Search client…" value={search}
          onChange={e => { setSearch(e.target.value); setPage(0); }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          sx={{ width: 220 }}
        />
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Metric</InputLabel>
          <Select value={metric} label="Metric" onChange={e => setMetric(e.target.value)}>
            <MenuItem value="totalSp">Total Sales</MenuItem>
            <MenuItem value="totalMargin">Margin</MenuItem>
            <MenuItem value="marginPercentage">Margin %</MenuItem>
          </Select>
        </FormControl>
        <Box sx={{ ml: 'auto' }}>
          <Button size="small" variant="outlined" startIcon={<FileDownloadIcon />} onClick={exportXlsx}>Export</Button>
        </Box>
      </Box>

      <TableContainer sx={{ maxHeight: 540, borderRadius: 2, border: '1px solid #e2e8f0' }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...th, minWidth: 180, position: 'sticky', left: 0, zIndex: 3 }}>
                <TableSortLabel active={orderBy === 'clientName'} direction={orderBy === 'clientName' ? order : 'asc'} onClick={() => handleSort('clientName')}>Client</TableSortLabel>
              </TableCell>
              {QUARTERS.map(q => (
                <TableCell key={q} align="right" sx={th}>
                  <TableSortLabel active={orderBy === `${q}.${metric}`} direction={orderBy === `${q}.${metric}` ? order : 'asc'} onClick={() => handleSort(`${q}.${metric}`)}>{q}</TableSortLabel>
                </TableCell>
              ))}
              <TableCell align="right" sx={{ ...th, bgcolor: '#eff6ff' }}>
                <TableSortLabel active={orderBy === metric} direction={orderBy === metric ? order : 'asc'} onClick={() => handleSort(metric)}>Total</TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginated.map(row => (
              <TableRow key={row.clientName} hover
                onClick={() => setDrill({ name: row.clientName, records: row._records })}
                sx={{ cursor: 'pointer', '&:hover': { bgcolor: alpha('#1d4ed8', 0.03) } }}>
                <TableCell sx={{ position: 'sticky', left: 0, bgcolor: '#fff', zIndex: 1, minWidth: 180 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="body2" fontWeight={700}>{row.clientName}</Typography>
                    <MuiTooltip title="View cost sheets & QoQ breakdown">
                      <OpenInNewIcon sx={{ fontSize: 13, color: '#94a3b8' }} />
                    </MuiTooltip>
                  </Box>
                  <Typography variant="caption" color="text.disabled">{row.recordCount} orders</Typography>
                </TableCell>
                {QUARTERS.map(q => (
                  <TableCell key={q} align="right">{renderVal(row[q][metric === 'totalMargin' ? 'margin' : metric], metric)}</TableCell>
                ))}
                <TableCell align="right" sx={{ bgcolor: '#f8fafc', fontWeight: 800 }}>{renderVal(row[metric], metric)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
          <TableFooter>
            <TableRow sx={{ '& td': { fontWeight: 800, borderTop: '2px solid #cbd5e1', bgcolor: '#f1f5f9' } }}>
              <TableCell>Grand Total</TableCell>
              {QUARTERS.map(q => (
                <TableCell key={q} align="right">{renderVal(summary[q][metric === 'totalMargin' ? 'margin' : metric], metric)}</TableCell>
              ))}
              <TableCell align="right">{renderVal(summary[metric], metric)}</TableCell>
            </TableRow>
          </TableFooter>
        </Table>
      </TableContainer>
      <TablePagination component="div" count={sorted.length} page={page} onPageChange={(_, p) => setPage(p)} rowsPerPage={rpp} onRowsPerPageChange={e => { setRpp(parseInt(e.target.value, 10)); setPage(0); }} rowsPerPageOptions={[10, 25, 50, 100]} />

      <CostsheetDrillDialog
        open={!!drill}
        onClose={() => setDrill(null)}
        title={drill?.name || ''}
        subtitle={`${drill?.records?.length || 0} cost sheets`}
        records={drill?.records || []}
        showQoQ
      />
    </Box>
  );
};

// ─── Generic N-Level Tree Table ──────────────────────────────────────────────────
  const TreeTable = ({ data, buildTree, columns, searchPlaceholder, exportName, onLeafClick, grandTotal }) => {
  const [expanded, setExpanded] = useState({});
  const [search,   setSearch]   = useState('');
  const toggle = (id) => setExpanded(p => ({ ...p, [id]: !p[id] }));
  const tree = useMemo(() => buildTree(data, search), [data, search, buildTree]);

  const exportXlsx = () => {
    const rows = [];
    const walk = (node, path) => { rows.push({ ...path, ...Object.fromEntries(columns.slice(1).map(c => [c.label, node[c.key]])) }); if (node._children) Object.entries(node._children).forEach(([k, v]) => walk(v, { ...path, [columns[0].label]: `  ${k}` })); };
    Object.entries(tree).forEach(([k, v]) => walk(v, { [columns[0].label]: k }));
    const ws = XLSX.utils.json_to_sheet(rows); const wb = XLSX.utils.book_new(); XLSX.utils.book_append_sheet(wb, ws, exportName); XLSX.writeFile(wb, `${exportName}.xlsx`);
  };

  const th = { fontWeight: 700, bgcolor: '#f8fafc', fontSize: '0.79rem' };

  // Recursive render function to automatically handle any depth without hardcoding
  const renderNode = (key, node, depth, path) => {
    const isLeaf = !node._children;
    const isExpanded = !!expanded[path];
    const rows = [];

    // Progressive indentation based on depth
    const pl = depth === 0 ? 2 : depth * 5;
    const isClickable = isLeaf && !!onLeafClick;

    // Resolve styling logic based on depth and leaf status
    let rowSx = {};
    if (isClickable) {
      rowSx = { cursor: 'pointer', '&:hover': { bgcolor: alpha('#1d4ed8', 0.06) } };
    } else if (!isLeaf) {
      rowSx = {
        bgcolor: depth === 0 ? alpha('#1d4ed8', 0.04) : alpha('#7c3aed', 0.03),
        '&:hover': { bgcolor: depth === 0 ? alpha('#1d4ed8', 0.08) : alpha('#7c3aed', 0.07) }
      };
    }

    rows.push(
      <TableRow
        key={path}
        hover={isLeaf}
        onClick={isClickable ? () => onLeafClick(key, node) : undefined}
        sx={rowSx}
      >
        <TableCell sx={{ pl }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {!isLeaf ? (
              <IconButton size="small" onClick={(e) => { e.stopPropagation(); toggle(path); }}>
                {isExpanded ? <KeyboardArrowDownIcon fontSize="small" /> : <KeyboardArrowRightIcon fontSize="small" />}
              </IconButton>
            ) : (
              <Box sx={{ width: 26 }} /> // Invisible spacer to align leaf text with icons
            )}
            <Typography
              variant="body2"
              fontWeight={isLeaf ? 400 : (depth === 0 ? 800 : 600)}
              color={isLeaf ? 'text.primary' : (depth === 0 ? 'text.primary' : 'text.secondary')}
              sx={{ fontSize: isLeaf ? '0.82rem' : '0.875rem' }}
            >
              {key}
            </Typography>
            {!isLeaf && <Chip size="small" label={node.recordCount} sx={{ height: 18, fontSize: '0.68rem', ml: 0.5 }} />}
            {isClickable && <Chip size="small" label="View" sx={{ height: 18, fontSize: '0.65rem', bgcolor: '#eff6ff', color: '#1d4ed8', ml: 0.5 }} />}
            {isLeaf && node.hasCorrection && <CorrectedBadge />}
          </Box>
        </TableCell>
        {columns.slice(1).map(c => (
          <TableCell
            key={c.label}
            align={c.align || 'left'}
            sx={{ fontWeight: isLeaf ? 400 : (depth === 0 ? 700 : 600), fontSize: isLeaf ? '0.82rem' : '0.875rem' }}
          >
            {c.render ? c.render(node[c.key]) : node[c.key]}
          </TableCell>
        ))}
        {onLeafClick && (
          <TableCell align="right">
            {isLeaf && <OpenInNewIcon fontSize="small" sx={{ color: '#94a3b8' }} />}
          </TableCell>
        )}
      </TableRow>
    );

    if (isExpanded && !isLeaf) {
      Object.entries(node._children).forEach(([childKey, childNode]) => {
        rows.push(...renderNode(childKey, childNode, depth + 1, `${path}-${childKey}`));
      });
    }
    return rows;
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, gap: 2, flexWrap: 'wrap' }}>
        <TextField size="small" placeholder={searchPlaceholder} value={search} onChange={e => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          sx={{ width: 350 }}
        />
        <Button size="small" variant="outlined" startIcon={<FileDownloadIcon />} onClick={exportXlsx}>Export Summary</Button>
      </Box>
      <TableContainer sx={{ borderRadius: 2, border: '1px solid #e2e8f0' }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ '& th': th }}>
              {columns.map(c => <TableCell key={c.label} align={c.align || 'left'}>{c.label}</TableCell>)}
              {onLeafClick && <TableCell sx={th} />}
            </TableRow>
          </TableHead>
          <TableBody>
            {Object.entries(tree).map(([k, v]) => renderNode(k, v, 0, k))}
          </TableBody>
          {/* GRAND TOTAL */}
          {grandTotal && (
            <TableFooter>
              <TableRow sx={{ '& td': { fontWeight: 800, borderTop: '2px solid #cbd5e1', bgcolor: '#f1f5f9', fontSize: '0.82rem' } }}>

                <TableCell>
                  <Typography variant="body2" fontWeight={800} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    Grand Total
                    <MuiTooltip title="Excludes inventory cost sheets." arrow>
                      <InfoOutlinedIcon fontSize="small" sx={{ cursor: 'pointer' }} />
                    </MuiTooltip>
                  </Typography>

                  <Typography variant="caption" color="text.disabled">
                    {grandTotal.recordCount} orders
                  </Typography>
                </TableCell>
                {columns.slice(1).map(c => (
                  <TableCell key={c.label} align={c.align || 'left'}>
                    {c.render ? c.render(grandTotal[c.key]) : grandTotal[c.key]}
                  </TableCell>
                ))}
                {onLeafClick && <TableCell />}
              </TableRow>
            </TableFooter>
          )}
        </Table>
      </TableContainer>
    </Box>
  );
};

// Tree builders
// DROPPED STATE: Now builds a 2-level hierarchy: City -> Employee
const buildRegionalTree = (data, search) => {
  const SPECIAL_CSF  = ['inventory', 'sample']; // internal now handled separately
  const tree = {};
  data.forEach(item => {
    const csf=(item.costsheetfor || '').toLowerCase();
    const c = item.salesPocCity || 'Unknown City', e = item.salesSpoc || 'Unknown Employee';
    const sp = item.totalSp || 0, m = item.margin || 0; const pp= item.totalPp ||0;

    if (csf === 'internal') {
      // ── Internal: CSF → Sales POC → Internal Category ──
      const csfLabel = 'Internal';
      const ic =( item.internalCategory || 'Unknown Internal Category').toLocaleUpperCase();

      if (!tree[csfLabel]) tree[csfLabel] = {
        totalSp: 0, totalMargin: 0, recordCount: 0, totalPp: 0,
        marginPercentage: 0, aov: 0, _children: {}
      };
      tree[csfLabel].totalSp     += sp;
      tree[csfLabel].totalPp     += pp;
      tree[csfLabel].totalMargin += m;
      tree[csfLabel].recordCount++;

      if (!tree[csfLabel]._children[e]) tree[csfLabel]._children[e] = {
        totalSp: 0, totalMargin: 0, recordCount: 0, totalPp: 0,
        marginPercentage: 0, aov: 0, _children: {}
      };
      tree[csfLabel]._children[e].totalSp     += sp;
      tree[csfLabel]._children[e].totalPp     += pp;
      tree[csfLabel]._children[e].totalMargin += m;
      tree[csfLabel]._children[e].recordCount++;

      if (!tree[csfLabel]._children[e]._children[ic]) tree[csfLabel]._children[e]._children[ic] = {
        totalSp: 0, totalMargin: 0, recordCount: 0, totalPp: 0,
        marginPercentage: 0, aov: 0, _records: []
      };
      tree[csfLabel]._children[e]._children[ic].totalSp     += sp;
      tree[csfLabel]._children[e]._children[ic].totalPp     += pp;
      tree[csfLabel]._children[e]._children[ic].totalMargin += m;
      tree[csfLabel]._children[e]._children[ic].recordCount++;
      tree[csfLabel]._children[e]._children[ic]._records.push(item);

    } else if (SPECIAL_CSF.includes(csf)) {
      // ── Inventory / Sample: CSF → City → Employee (unchanged) ──
      const csfLabel = csf.charAt(0).toUpperCase() + csf.slice(1);

      if (!tree[csfLabel]) tree[csfLabel] = {
        totalSp: 0, totalMargin: 0, recordCount: 0, totalPp:0,
        marginPercentage: 0, aov: 0, _children: {}
      };
      tree[csfLabel].totalSp     += sp;
      tree[csfLabel].totalPp     += pp;
      tree[csfLabel].totalMargin += m;
      tree[csfLabel].recordCount++;

      if (!tree[csfLabel]._children[c]) tree[csfLabel]._children[c] = {
        totalSp: 0, totalMargin: 0, recordCount: 0, totalPp:0,
        marginPercentage: 0, aov: 0, _children: {}
      };
      tree[csfLabel]._children[c].totalSp     += sp;
      tree[csfLabel]._children[c].totalPp     += pp;
      tree[csfLabel]._children[c].totalMargin += m;
      tree[csfLabel]._children[c].recordCount++;

      if (!tree[csfLabel]._children[c]._children[e]) tree[csfLabel]._children[c]._children[e] = {
        totalSp: 0, totalMargin: 0, recordCount: 0, totalPp:0,
        marginPercentage: 0, aov: 0, _records: []
      };
      tree[csfLabel]._children[c]._children[e].totalSp     += sp;
      tree[csfLabel]._children[c]._children[e].totalPp     += pp;
      tree[csfLabel]._children[c]._children[e].totalMargin += m;
      tree[csfLabel]._children[c]._children[e].recordCount++;
      tree[csfLabel]._children[c]._children[e]._records.push(item);
    } else{

    // Level 1: City
    if (!tree[c]) tree[c] = { totalSp: 0, totalMargin: 0, totalPp:0, recordCount: 0, marginPercentage: 0, aov: 0, _children: {} };
    tree[c].totalSp += sp; tree[c].totalMargin += m; tree[c].recordCount++;

    // Level 2: Employee (Leaf node containing the drill-down records)
    if (!tree[c]._children[e]) tree[c]._children[e] = { totalSp: 0, totalMargin: 0, recordCount: 0, marginPercentage: 0, aov: 0, _records: [] };
    tree[c]._children[e].totalSp += sp;
    tree[c]._children[e].totalPp += pp;
    tree[c]._children[e].totalMargin += m;
    tree[c]._children[e].recordCount++;
    tree[c]._children[e]._records.push(item);
    tree[c]._children[e].hasCorrection = tree[c]._children[e].hasCorrection || item.spocAnyCorrected;
    }
  });


  const calc = n => { n.marginPercentage = n.totalSp > 0 ? (n.totalMargin / n.totalSp) * 100 : 0; n.aov = n.recordCount > 0 ? n.totalSp / n.recordCount : 0; if (n._children) Object.values(n._children).forEach(calc); };
  Object.values(tree).forEach(calc);
  const SPECIAL_LABELS = ['Internal', 'Inventory', 'Sample'];
  const result = {};
   [
    ...Object.keys(tree).filter(k => !SPECIAL_LABELS.includes(k)).sort(),
    ...SPECIAL_LABELS.filter(k => tree[k]),
    
  ].filter(k => !search || k.toLowerCase().includes(search.toLowerCase()))
   .forEach(k => { result[k] = tree[k]; });

  // Object.keys(tree).sort().filter(k => !search || k.toLowerCase().includes(search.toLowerCase())).forEach(k => { result[k] = tree[k]; });
  return result;
};

// Maintains the original 3-level hierarchy: Category -> Subcategory -> Brand
const buildProductTree = (data, search) => {
  const tree = {};
  data.forEach(item => {
    const cat = item.category, sub = item.subcategory, br = item.brand;
    const sp = item.totalSp || 0, m = item.margin || 0, qty = item.qty || 0;
    if (!tree[cat]) tree[cat] = { totalSp: 0, totalMargin: 0, totalQty: 0, recordCount: 0, marginPercentage: 0, _children: {} };
    tree[cat].totalSp += sp; tree[cat].totalMargin += m; tree[cat].totalQty += qty; tree[cat].recordCount++;
    if (!tree[cat]._children[sub]) tree[cat]._children[sub] = { totalSp: 0, totalMargin: 0, totalQty: 0, recordCount: 0, marginPercentage: 0, _children: {} };
    tree[cat]._children[sub].totalSp += sp; tree[cat]._children[sub].totalMargin += m; tree[cat]._children[sub].totalQty += qty; tree[cat]._children[sub].recordCount++;
    if (!tree[cat]._children[sub]._children[br]) tree[cat]._children[sub]._children[br] = { totalSp: 0, totalMargin: 0, totalQty: 0, recordCount: 0, marginPercentage: 0 };
    tree[cat]._children[sub]._children[br].totalSp += sp; tree[cat]._children[sub]._children[br].totalMargin += m; tree[cat]._children[sub]._children[br].totalQty += qty; tree[cat]._children[sub]._children[br].recordCount++;
  });
  const calc = n => { n.marginPercentage = n.totalSp > 0 ? (n.totalMargin / n.totalSp) * 100 : 0; if (n._children) Object.values(n._children).forEach(calc); };
  Object.values(tree).forEach(calc);
  //enables search by category, subcategory, or brand
  if(!search) {
     const sorted ={}
     Object.keys(tree).sort().forEach(k => { sorted[k] = tree[k]; });
      return sorted;
  }
  else {
    const result = {};
    Object.keys(tree).sort().forEach(cat => {
      if(cat.toLowerCase().includes(search.toLowerCase())) {
        result[cat] = tree[cat];
      } else {
        const subResult = {};
        Object.keys(tree[cat]._children).sort().forEach(sub => {
          if(sub.toLowerCase().includes(search.toLowerCase())) {
            subResult[sub] = tree[cat]._children[sub];

          } else {
            const brandResult = {};
            Object.keys(tree[cat]._children[sub]._children).sort().forEach(br => {
              if(br.toLowerCase().includes(search.toLowerCase())) {
                brandResult[br] = tree[cat]._children[sub]._children[br];
              }
            });
            if(Object.keys(brandResult).length) {
              subResult[sub] = { ...tree[cat]._children[sub], _children: brandResult };
            }
          }
        });
        if(Object.keys(subResult).length) {
          result[cat] = { ...tree[cat], _children: subResult };
        }
      }
    });
    return result;
  }
};

// ─── Vendor table ─────────────────────────────────────────────────────────────────
const VendorTable = ({ data }) => {
  const [page,    setPage]  = useState(0);
  const [rpp,     setRpp]   = useState(10);
  const [search,  setSearch]= useState('');
  const [orderBy, setOB]    = useState('totalPurchase');
  const [order,   setOrder] = useState('desc');
  const [drill,   setDrill] = useState(null);

  const rows = useMemo(() => {
    const v = {};
    data.forEach(item => {
      item.vendorName.split(', ').filter(Boolean).forEach(name => {
        if (!v[name]) v[name] = { totalPurchase: 0, totalSp: 0, margin: 0, orderCount: 0, clients: new Set(), _records: [] };
        v[name].totalPurchase += item.totalPp;
        v[name].totalSp       += item.totalSp;
        v[name].margin        += item.margin;
        v[name].orderCount++;
        v[name].clients.add(item.clientName);
        v[name]._records.push(item);
      });
    });
    return Object.entries(v).map(([name, d]) => ({
      name, totalPurchase: d.totalPurchase, totalSp: d.totalSp, margin: d.margin,
      orderCount: d.orderCount, clientCount: d.clients.size, _records: d._records,
    }));
  }, [data]);

  const sorted = useMemo(() =>
    [...rows].filter(r => r.name.toLowerCase().includes(search.toLowerCase()))
      .sort((a, b) => order === 'asc' ? a[orderBy] - b[orderBy] : b[orderBy] - a[orderBy]),
  [rows, search, order, orderBy]);

  const handleSort = (col) => { setOrder(orderBy === col && order === 'asc' ? 'desc' : 'asc'); setOB(col); };
  const th = { fontWeight: 700, bgcolor: '#f8fafc', fontSize: '0.79rem' };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, gap: 2, flexWrap: 'wrap' }}>
        <TextField size="small" placeholder="Search vendor…" value={search} onChange={e => { setSearch(e.target.value); setPage(0); }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          sx={{ width: 260 }}
        />
        <Button size="small" variant="outlined" startIcon={<FileDownloadIcon />} onClick={() => {
          const ws = XLSX.utils.json_to_sheet(sorted.map(r => ({ Vendor: r.name, 'Cost (INR)': r.totalPurchase, 'Sales (INR)': r.totalSp, 'Margin (INR)': r.margin, Orders: r.orderCount, 'Unique Clients': r.clientCount })));
          const wb = XLSX.utils.book_new(); XLSX.utils.book_append_sheet(wb, ws, 'Vendors'); XLSX.writeFile(wb, 'vendors.xlsx');
        }}>Export</Button>
      </Box>
      <TableContainer sx={{ borderRadius: 2, border: '1px solid #e2e8f0' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={th}>#</TableCell>
              <TableCell sx={th}>Vendor</TableCell>
              {[
                { label: 'Total Cost', key: 'totalPurchase' },
                { label: 'Sales',      key: 'totalSp' },
                { label: 'Margin',     key: 'margin' },
                { label: 'Orders',     key: 'orderCount' },
                { label: 'Clients',    key: 'clientCount' },
              ].map(c => (
                <TableCell key={c.key} align="right" sx={th}>
                  <TableSortLabel active={orderBy === c.key} direction={orderBy === c.key ? order : 'asc'} onClick={() => handleSort(c.key)}>{c.label}</TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sorted.slice(page * rpp, page * rpp + rpp).map((r, i) => (
              <TableRow key={r.name} hover
                onClick={() => setDrill({ name: r.name, records: r._records })}
                sx={{ cursor: 'pointer' }}>
                <TableCell sx={{ color: '#94a3b8', fontSize: '0.8rem' }}>{page * rpp + i + 1}</TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="body2" fontWeight={600}>{r.name}</Typography>
                    <MuiTooltip title="View cost sheets & QoQ breakdown"><OpenInNewIcon sx={{ fontSize: 13, color: '#94a3b8' }} /></MuiTooltip>
                  </Box>
                </TableCell>
                <TableCell align="right">{fmtShort(r.totalPurchase)}</TableCell>
                <TableCell align="right">{fmtShort(r.totalSp)}</TableCell>
                <TableCell align="right" sx={{ color: r.margin >= 0 ? '#15803d' : '#dc2626', fontWeight: 600 }}>{fmtShort(r.margin)}</TableCell>
                <TableCell align="right">{r.orderCount}</TableCell>
                <TableCell align="right">
                  <Chip size="small" label={r.clientCount} sx={{ bgcolor: '#eff6ff', color: '#1d4ed8', fontWeight: 700, height: 20, fontSize: '0.72rem' }} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination component="div" count={sorted.length} page={page} onPageChange={(_, p) => setPage(p)} rowsPerPage={rpp} onRowsPerPageChange={e => { setRpp(parseInt(e.target.value, 10)); setPage(0); }} rowsPerPageOptions={[10, 25, 50]} />

      <CostsheetDrillDialog
        open={!!drill}
        onClose={() => setDrill(null)}
        title={drill?.name || ''}
        subtitle={`Vendor · ${drill?.records?.length || 0} cost sheets`}
        records={drill?.records || []}
        showQoQ
      />
    </Box>
  );
};

// ─── Legal Vendor pivot table — groups by mapped legal vendor name, falls back to raw name ──
// Note: a single cost sheet can list multiple raw vendor names (comma-joined), so — mirroring
// VendorTable's behaviour above — each of an entry's vendor names receives the entry's full
// totals. This can double-count totals across vendors on multi-vendor cost sheets by design.
const LegalVendorPivotTable = ({ data, rawToLegalVendorMap }) => {
  const [orderBy, setOrderBy] = useState('totalSp');
  const [order, setOrder] = useState('desc');
  const [page, setPage] = useState(0);
  const [rpp, setRpp] = useState(10);
  const [metric, setMetric] = useState('totalSp');
  const [search, setSearch] = useState('');
  const [filterMapped, setFilterMapped] = useState('all'); // 'all' | 'mapped' | 'unmapped'
  const [drill, setDrill] = useState(null);

  const { rows, summary } = useMemo(() => {
    const g = {};
    data.forEach(item => {
      const rawNames = (item.vendorName || '').split(', ').filter(Boolean);
      if (!rawNames.length) return;
      rawNames.forEach(rawName => {
        const legalName = rawToLegalVendorMap[rawName];
        const k = legalName || rawName;
        const isMapped = !!legalName;
        if (!g[k]) {
          g[k] = { vendorName: k, isMapped, totalSp: 0, totalPp: 0, totalMargin: 0, recordCount: 0, _records: [] };
          QUARTERS.forEach(q => { g[k][q] = { totalSp: 0, totalPp: 0, margin: 0 }; });
        }
        const c = g[k], q = item.quarter;
        c[q].totalSp += item.totalSp; c[q].totalPp += item.totalPp; c[q].margin += item.margin;
        c.totalSp += item.totalSp; c.totalPp += item.totalPp; c.totalMargin += item.margin; c.recordCount++;
        c._records.push(item);
      });
    });
    const grand = { totalSp: 0, totalPp: 0, totalMargin: 0, vendorCount: 0 };
    QUARTERS.forEach(q => { grand[q] = { totalSp: 0, totalPp: 0, margin: 0 }; });
    Object.values(g).forEach(c => {
      c.marginPercentage = c.totalSp > 0 ? (c.totalMargin / c.totalSp) * 100 : 0;
      QUARTERS.forEach(q => {
        c[q].marginPercentage = c[q].totalSp > 0 ? (c[q].margin / c[q].totalSp) * 100 : 0;
        grand[q].totalSp += c[q].totalSp; grand[q].totalPp += c[q].totalPp; grand[q].margin += c[q].margin;
      });
      grand.totalSp += c.totalSp; grand.totalPp += c.totalPp; grand.totalMargin += c.totalMargin; grand.vendorCount++;
    });
    grand.marginPercentage = grand.totalSp > 0 ? (grand.totalMargin / grand.totalSp) * 100 : 0;
    QUARTERS.forEach(q => { grand[q].marginPercentage = grand[q].totalSp > 0 ? (grand[q].margin / grand[q].totalSp) * 100 : 0; });
    return { rows: Object.values(g), summary: grand };
  }, [data, rawToLegalVendorMap]);

  const sorted = useMemo(() => {
    return [...rows]
      .filter(r => !search || r.vendorName.toLowerCase().includes(search.toLowerCase()))
      .filter(r => filterMapped === 'all' || (filterMapped === 'mapped' ? r.isMapped : !r.isMapped))
      .sort((a, b) => {
        if (orderBy === 'vendorName') return order === 'asc' ? a.vendorName.localeCompare(b.vendorName) : b.vendorName.localeCompare(a.vendorName);
        const [q, m] = orderBy.includes('.') ? orderBy.split('.') : [null, orderBy];
        const va = q ? a[q][m] : a[m], vb = q ? b[q][m] : b[m];
        return order === 'asc' ? va - vb : vb - va;
      });
  }, [rows, order, orderBy, search, filterMapped]);

  const paginated = sorted.slice(page * rpp, page * rpp + rpp);
  const handleSort = (p) => { setOrder(orderBy === p && order === 'asc' ? 'desc' : 'asc'); setOrderBy(p); };

  const renderVal = (val, m) => {
    if (m === 'marginPercentage') return <MarginChip value={val} />;
    return <Typography variant="body2" fontWeight={600}>{fmtShort(val)}</Typography>;
  };

  const exportXlsx = () => {
    const r = sorted.map(row => ({
      'Legal Vendor': row.vendorName, 'Mapped': row.isMapped ? 'Yes' : 'No',
      'Q1 Sales': row.Q1.totalSp, 'Q1 Margin': row.Q1.margin, 'Q1 Margin%': row.Q1.marginPercentage,
      'Q2 Sales': row.Q2.totalSp, 'Q2 Margin': row.Q2.margin, 'Q2 Margin%': row.Q2.marginPercentage,
      'Q3 Sales': row.Q3.totalSp, 'Q3 Margin': row.Q3.margin, 'Q3 Margin%': row.Q3.marginPercentage,
      'Q4 Sales': row.Q4.totalSp, 'Q4 Margin': row.Q4.margin, 'Q4 Margin%': row.Q4.marginPercentage,
      'Total Sales': row.totalSp, 'Total Margin': row.totalMargin, 'Margin%': row.marginPercentage,
    }));
    const ws = XLSX.utils.json_to_sheet(r); const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'LegalVendors'); XLSX.writeFile(wb, 'legal_vendor_analytics.xlsx');
  };

  const th = { fontWeight: 700, bgcolor: '#f8fafc', fontSize: '0.79rem', whiteSpace: 'nowrap' };

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField size="small" placeholder="Search legal vendor…" value={search}
          onChange={e => { setSearch(e.target.value); setPage(0); }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          sx={{ width: 220 }}
        />
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Metric</InputLabel>
          <Select value={metric} label="Metric" onChange={e => setMetric(e.target.value)}>
            <MenuItem value="totalSp">Total Sales</MenuItem>
            <MenuItem value="totalMargin">Margin</MenuItem>
            <MenuItem value="marginPercentage">Margin %</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Mapping</InputLabel>
          <Select value={filterMapped} label="Mapping" onChange={e => { setFilterMapped(e.target.value); setPage(0); }}>
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="mapped">Mapped Only</MenuItem>
            <MenuItem value="unmapped">Unmapped Only</MenuItem>
          </Select>
        </FormControl>
        <Box sx={{ ml: 'auto' }}>
          <Button size="small" variant="outlined" startIcon={<FileDownloadIcon />} onClick={exportXlsx}>Export</Button>
        </Box>
      </Box>

      <TableContainer sx={{ maxHeight: 540, borderRadius: 2, border: '1px solid #e2e8f0' }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...th, minWidth: 200, position: 'sticky', left: 0, zIndex: 3 }}>
                <TableSortLabel active={orderBy === 'vendorName'} direction={orderBy === 'vendorName' ? order : 'asc'} onClick={() => handleSort('vendorName')}>Legal Vendor</TableSortLabel>
              </TableCell>
              {QUARTERS.map(q => (
                <TableCell key={q} align="right" sx={th}>
                  <TableSortLabel active={orderBy === `${q}.${metric}`} direction={orderBy === `${q}.${metric}` ? order : 'asc'} onClick={() => handleSort(`${q}.${metric}`)}>{q}</TableSortLabel>
                </TableCell>
              ))}
              <TableCell align="right" sx={{ ...th, bgcolor: '#eff6ff' }}>
                <TableSortLabel active={orderBy === metric} direction={orderBy === metric ? order : 'asc'} onClick={() => handleSort(metric)}>Total</TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginated.map(row => (
              <TableRow key={row.vendorName} hover
                onClick={() => setDrill({ name: row.vendorName, records: row._records })}
                sx={{ cursor: 'pointer', '&:hover': { bgcolor: alpha('#0f766e', 0.03) } }}>
                <TableCell sx={{ position: 'sticky', left: 0, bgcolor: '#fff', zIndex: 1, minWidth: 200 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="body2" fontWeight={700}>{row.vendorName}</Typography>
                    {!row.isMapped && (
                      <Chip size="small" label="Unmapped" sx={{ height: 18, fontSize: '0.65rem', bgcolor: '#fff7ed', color: '#c2410c' }} />
                    )}
                    <MuiTooltip title="View cost sheets & QoQ breakdown">
                      <OpenInNewIcon sx={{ fontSize: 13, color: '#94a3b8' }} />
                    </MuiTooltip>
                  </Box>
                  <Typography variant="caption" color="text.disabled">{row.recordCount} orders</Typography>
                </TableCell>
                {QUARTERS.map(q => (
                  <TableCell key={q} align="right">{renderVal(row[q][metric === 'totalMargin' ? 'margin' : metric], metric)}</TableCell>
                ))}
                <TableCell align="right" sx={{ bgcolor: '#f8fafc', fontWeight: 800 }}>{renderVal(row[metric], metric)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
          <TableFooter>
            <TableRow sx={{ '& td': { fontWeight: 800, borderTop: '2px solid #cbd5e1', bgcolor: '#f1f5f9' } }}>
              <TableCell>Grand Total</TableCell>
              {QUARTERS.map(q => (
                <TableCell key={q} align="right">{renderVal(summary[q][metric === 'totalMargin' ? 'margin' : metric], metric)}</TableCell>
              ))}
              <TableCell align="right">{renderVal(summary[metric], metric)}</TableCell>
            </TableRow>
          </TableFooter>
        </Table>
      </TableContainer>
      <TablePagination component="div" count={sorted.length} page={page} onPageChange={(_, p) => setPage(p)} rowsPerPage={rpp} onRowsPerPageChange={e => { setRpp(parseInt(e.target.value, 10)); setPage(0); }} rowsPerPageOptions={[10, 25, 50, 100]} />

      <CostsheetDrillDialog
        open={!!drill}
        onClose={() => setDrill(null)}
        title={drill?.name || ''}
        subtitle={`${drill?.records?.length || 0} cost sheets`}
        records={drill?.records || []}
        showQoQ
      />
    </Box>
  );
};

// ─── Main Dashboard ───────────────────────────────────────────────────────────────
const AnalyticsDashboard = () => {
  const theme    = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [flatData,        setFlatData]        = useState([]);
  const [productFlatData, setProductFlatData] = useState([]);
  const [prevFlatData,    setPrevFlatData]    = useState(null);
  const [selectedYear,    setSelectedYear]    = useState('all');
  const [selectedQuarter, setSelectedQuarter] = useState('all');
  const [dateRange,       setDateRange]       = useState([{ startDate: null, endDate: null, key: 'selection' }]);
  const [dateModal,       setDateModal]       = useState(false);
  const [activeTab,       setActiveTab]       = useState(0);
  const [filterDrawer,    setFilterDrawer]    = useState(false);
  const [selectedEmployee,setSelectedEmployee]= useState(null);
  const hasFetchedRef = useRef(false);

  const { instance, accounts } = useMsal();
  const account = accounts[0];
  const [usersMap, setUsersMap] = useState({});
  // Slim cost sheets: just the fields processRaw reads, built once on the server for everyone
  const [analyticsOrders, setAnalyticsOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [legalClients, setLegalClients] = useState([]);
  const [legalVendors, setLegalVendors] = useState([]);

  useEffect(() => {
    const fetchLegalClients = async () => {
      try {
        const token = await getApiToken(instance, account);
        const res = await fetch(`${backend_api_base_url}/api/v2/clients`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        if (!res.ok) return;
        const data = await res.json();
        setLegalClients(data || []);
      } catch (e) {
        console.warn('[Analytics] Could not load legal clients:', e.message);
      }
    };
    if (account) fetchLegalClients();
  }, [account]);

  useEffect(() => {
    const fetchLegalVendors = async () => {
      try {
        const token = await getApiToken(instance, account);
        const res = await fetch(`${backend_api_base_url}/api/v2/vendors`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        if (!res.ok) return;
        const data = await res.json();
        setLegalVendors(data || []);
      } catch (e) {
        console.warn('[Analytics] Could not load legal vendors:', e.message);
      }
    };
    if (account) fetchLegalVendors();
  }, [account]);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const token = await getApiToken(instance, account);
        const res = await fetch(`${backend_api_base_url}/api/v2/oms-users`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        if (!res.ok) return;
        const data = await res.json();
        const map = {};
        data.forEach(u => {
          if (u.displayName) map[u.displayName.toLowerCase().trim()] = u;
          // if (u.email) {
          //   const prefix = u.email.split('@')[0].toLowerCase().trim();
          //   if (!map[prefix]) map[prefix] = u;
          // }
          if (u.email) {
            const emailLower = u.email.toLowerCase().trim();
            // Full email lookup
            if (!map[emailLower]) {
              map[emailLower] = u;
            }
            // Local-part lookup (before @)
            const prefix = emailLower.split('@')[0];
            if (!map[prefix]) {
              map[prefix] = u;
            }
          }
          if (u.upn) {
            const upn = u.upn.toLowerCase().trim();
            if (!map[upn]) {
              map[upn] = u;
            }
          }
        });
        setUsersMap(map);
      } catch (e) {
        console.warn('[Analytics] Could not load users table:', e.message);
      }
    };
    if (account) fetchUsers();
  }, [account]);

  useEffect(() => {
    if (!account && !window.__E2E_TEST__) return;
    let cancelled = false;
    hasFetchedRef.current = true;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await fetchAnalyticsOrders(instance, account);
        if (!cancelled) setAnalyticsOrders(data || []);
      } catch (err) {
        if (!cancelled) setError(`Failed to load analytics data: ${err.message}`);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [account, instance]);

  const allRawData   = analyticsOrders;
  const rawToLegalMap = useMemo(() => {
    const map = {};
    legalClients.forEach(c => {
      (c.rawClientNames || []).forEach(rn => { map[rn] = c.legalClientName; });
    });
    return map;
  }, [legalClients])

  const rawToLegalVendorMap = useMemo(() => {
    const map = {};
    legalVendors.forEach(v => {
      (v.rawVendorNames || []).forEach(rn => { map[rn] = v.legalVendorName; });
    });
    return map;
  }, [legalVendors])
  
  const uniqueYears  = useMemo(() => {
    const s = new Set(); allRawData.forEach(e => { if (e.date) s.add(getFinancialYear(e.date)); });
    return ['all', ...Array.from(s).sort((a, b) => b - a)];
  }, [allRawData]);

  useEffect(()=>{
    if(uniqueYears.length>1){
          setSelectedYear(prev => prev === 'all' ? uniqueYears[1] : prev);
    }
  }, [uniqueYears])

  const clearAll = () => { setSelectedYear('all'); setSelectedQuarter('all'); setDateRange([{ startDate: null, endDate: null, key: 'selection' }]); };
  const activeFilters = [selectedYear !== 'all', selectedQuarter !== 'all', !!dateRange[0].startDate].filter(Boolean).length;

  useEffect(() => {
    const { flat, product } = processRaw(allRawData, selectedYear, selectedQuarter, dateRange, usersMap);
    setFlatData(flat); setProductFlatData(product);
  }, [allRawData, selectedYear, selectedQuarter, dateRange, usersMap]);

  useEffect(() => {
    if (selectedYear === 'all' && selectedQuarter === 'all' && !dateRange[0].startDate) { setPrevFlatData(null); return; }
    const qOrder = ['Q1', 'Q2', 'Q3', 'Q4']; let prevYear = selectedYear, prevQ = selectedQuarter;
    if (selectedQuarter !== 'all') { const idx = qOrder.indexOf(selectedQuarter); if (idx === 0) { prevYear = selectedYear !== 'all' ? selectedYear - 1 : 'all'; prevQ = 'Q4'; } else prevQ = qOrder[idx - 1]; }
    else if (selectedYear !== 'all') prevYear = selectedYear - 1;
    let prevRange = [{ startDate: null, endDate: null, key: 'selection' }];
    if (dateRange[0].startDate && dateRange[0].endDate) { const span = dateRange[0].endDate - dateRange[0].startDate; prevRange = [{ startDate: new Date(dateRange[0].startDate - span), endDate: new Date(dateRange[0].startDate - 1), key: 'selection' }]; }
    const { flat: pFlat } = processRaw(allRawData, prevYear, prevQ, prevRange, usersMap);
    setPrevFlatData(pFlat.length ? pFlat : null);
  }, [allRawData, selectedYear, selectedQuarter, dateRange, usersMap]);

  // Overview tab must exclude Inventory cost sheets — same rule Regional tab's grand total uses
  const overviewFlatData = useMemo(
    () => flatData.filter(d => (d.costsheetfor || '').toLowerCase() !== 'inventory'),
    [flatData]
  );
  const overviewPrevFlatData = useMemo(
    () => prevFlatData ? prevFlatData.filter(d => (d.costsheetfor || '').toLowerCase() !== 'inventory') : null,
    [prevFlatData]
  );
  const overviewProductFlatData = useMemo(
    () => productFlatData.filter(d => (d.costsheetfor || '').toLowerCase() !== 'inventory'),
    [productFlatData]
  );


  const summary     = useMemo(() => calcSummary(overviewFlatData),     [overviewFlatData]);
  const prevSummary = useMemo(() => overviewPrevFlatData ? calcSummary(overviewPrevFlatData) : null, [overviewPrevFlatData]);

  const font = "'Sora','Nunito','system-ui',sans-serif";
  const P = {
    blue:   ['rgba(29,78,216,0.75)',  '#1d4ed8'],
    orange: ['rgba(194,65,12,0.75)',  '#c2410c'],
    purple: ['rgba(109,40,217,0.75)', '#6d28d9'],
    teal:   ['rgba(15,118,110,0.75)', '#0f766e'],
    multi:  ['rgba(29,78,216,0.75)', 'rgba(21,128,61,0.75)', 'rgba(194,65,12,0.75)', 'rgba(109,40,217,0.75)', 'rgba(15,118,110,0.75)', 'rgba(190,18,60,0.75)', 'rgba(161,98,7,0.75)', 'rgba(3,105,161,0.75)'],
  };

  const baseOpts = useMemo(() => ({
    responsive: true, maintainAspectRatio: false,
    plugins: { legend: { display: false }, tooltip: { backgroundColor: '#0f172a', padding: 10, cornerRadius: 10, titleFont: { size: 13, family: font, weight: '700' }, bodyFont: { size: 12, family: font }, callbacks: { label: ctx => ` ${fmtShort(ctx.parsed.y ?? ctx.parsed)}` } } },
    scales: {
      x: {
        grid: { display: false },
        ticks: {
          font: { size: isMobile ? 9 : 11, family: font },
          maxRotation: 45,
          minRotation: 45,
          callback: function(value) {
            const label = this.getLabelForValue(value) || '';
            if (typeof label === 'string' && label.length > 12) {
              return label.substring(0, 12) + '...';
            }
            return label;
          }
        }
      },
      y: { beginAtZero: true, grid: { color: 'rgba(0,0,0,0.04)', borderDash: [4, 4] }, ticks: { font: { size: isMobile ? 9 : 11, family: font }, callback: v => fmtShort(v) } }
    },
  }), [isMobile]);

  const pieOpts = useMemo(() => ({
    responsive: true, maintainAspectRatio: false,
    plugins: { legend: { position: isMobile ? 'bottom' : 'right', labels: { font: { size: 11, family: font }, boxWidth: 13, padding: 10 } }, tooltip: { backgroundColor: '#0f172a', padding: 10, cornerRadius: 10, callbacks: { label: ctx => ` ${ctx.label}: ${fmtShort(ctx.parsed)}` } } },
  }), [isMobile]);

  const dualOpts = useMemo(() => ({
    ...baseOpts,
    plugins: { ...baseOpts.plugins, legend: { display: true, position: 'top', labels: { font: { size: 11, family: font }, boxWidth: 13 } } },
    scales: { x: baseOpts.scales.x, y: { ...baseOpts.scales.y, position: 'left', title: { display: !isMobile, text: 'Sales', font: { size: 10 } } }, y1: { ...baseOpts.scales.y, position: 'right', title: { display: !isMobile, text: 'Margin', font: { size: 10 } }, grid: { drawOnChartArea: false } } },
  }), [baseOpts, isMobile]);

  const mkBar = useCallback((labels, values, colorArr, label) => ({
    labels,
    datasets: [{ label, data: values, backgroundColor: colorArr[0], borderColor: colorArr[1], borderWidth: 1.5, borderRadius: 8, barThickness: isMobile ? 16 : 26 }],
  }), [isMobile]);

  const chartData = useMemo(() => {
    const byCli = {}, bySpoc = {}, byQ = {}, byMonth = {}, byCsf = {}, byMarCli = {};
    flatData.forEach(d => {
      byCli[d.clientName]    = (byCli[d.clientName]    || 0) + d.totalSp;
      bySpoc[d.salesSpoc]    = (bySpoc[d.salesSpoc]    || 0) + d.totalSp;
      byQ[d.quarter]         = (byQ[d.quarter]          || 0) + d.totalSp;
      byCsf[d.costsheetfor]  = (byCsf[d.costsheetfor]  || 0) + d.totalSp;
      byMarCli[d.clientName] = (byMarCli[d.clientName] || 0) + d.margin;
      const ym = `${new Date(d.date).getFullYear()}-${String(new Date(d.date).getMonth() + 1).padStart(2, '0')}`;
      if (!byMonth[ym]) byMonth[ym] = { sales: 0, margin: 0 };
      byMonth[ym].sales += d.totalSp; byMonth[ym].margin += d.margin;
    });
    const t5Cli = Object.entries(byCli).sort(([, a], [, b]) => b - a).slice(0, 5);
    const t5Sp  = Object.entries(bySpoc).sort(([, a], [, b]) => b - a).slice(0, 5);
    const t5Mar = Object.entries(byMarCli).sort(([, a], [, b]) => b - a).slice(0, 5);
    const qOrd  = ['Q1', 'Q2', 'Q3', 'Q4'];
    const months = Object.keys(byMonth).sort();
    return {
      salesByClient:  mkBar(t5Cli.map(([k]) => k), t5Cli.map(([, v]) => v), P.blue,   'Sales'),
      salesBySpoc:    mkBar(t5Sp.map(([k]) => k),  t5Sp.map(([, v]) => v),  P.teal,   'Sales'),
      marginByClient: mkBar(t5Mar.map(([k]) => k), t5Mar.map(([, v]) => v), P.purple, 'Margin'),
      salesByQuarter: mkBar(qOrd.filter(q => byQ[q]), qOrd.filter(q => byQ[q]).map(q => byQ[q]), P.orange, 'Sales'),
      salesByCsf: { labels: Object.keys(byCsf), datasets: [{ data: Object.values(byCsf), backgroundColor: P.multi, borderWidth: 1.5 }] },
      monthlySales: {
        labels: months.map(ym => { const [y, m] = ym.split('-'); return new Date(y, m - 1).toLocaleString('default', { month: 'short', year: '2-digit' }); }),
        datasets: [
          { label: 'Sales',  data: months.map(ym => byMonth[ym].sales),  borderColor: P.blue[1],  backgroundColor: 'rgba(29,78,216,0.07)',  tension: 0.4, fill: true, yAxisID: 'y' },
          { label: 'Margin', data: months.map(ym => byMonth[ym].margin), borderColor: '#dc2626',   backgroundColor: 'rgba(220,38,38,0.07)',  tension: 0.4, fill: true, yAxisID: 'y1' },
        ],
      },
    };
  }, [overviewFlatData, mkBar]);

  const productChartData = useMemo(() => {
    const byBr = {}, byCat = {};
    productFlatData.forEach(d => { byBr[d.brand] = (byBr[d.brand] || 0) + d.totalSp; byCat[d.category] = (byCat[d.category] || 0) + d.totalSp; });
    const t10Br  = Object.entries(byBr).sort(([, a], [, b]) => b - a).slice(0, 10);
    const t10Cat = Object.entries(byCat).sort(([, a], [, b]) => b - a).slice(0, 10);
    return {
      salesByBrand:    mkBar(t10Br.map(([k]) => k),  t10Br.map(([, v]) => v),  P.purple, 'Sales'),
      salesByCategory: mkBar(t10Cat.map(([k]) => k), t10Cat.map(([, v]) => v), P.orange, 'Sales'),
    };
  }, [overviewProductFlatData, mkBar]);

  const tabs = [
    { label: 'Overview',  icon: <BarChartIcon fontSize="small" /> },
    { label: 'Clients',   icon: <PeopleIcon fontSize="small" /> },
    { label: 'Products',  icon: <InventoryIcon fontSize="small" /> },
    { label: 'Regional',  icon: <MapIcon fontSize="small" /> },
    { label: 'Vendors',   icon: <LocalShippingIcon fontSize="small" /> },
     { label: 'Legal Client', icon: <BusinessIcon fontSize="small" /> },
     { label: 'Legal Vendor', icon: <StorefrontIcon fontSize="small" /> },
  ];
  const chartH = isMobile ? 200 : 280;

  // ─── Custom Handle Print ────────────────────────────────────────────────────────
  // Temporarily changes the document title so the browser downloads it with the correct name
  const handlePrint = () => {
    const originalTitle = document.title;

    // Generates date in YYYY-MM-DD format
    const dateStr = new Date().toISOString().split('T')[0];
    document.title = `BPAnalyticsOMS_${dateStr}`;

    window.print();

    // Set timeout ensures the browser catches the new title before reverting
    setTimeout(() => {
      document.title = originalTitle;
    }, 500);
  };

  const FilterControls = () => (
    <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 2, alignItems: isMobile ? 'stretch' : 'center', flexWrap: 'wrap' }}>
      <FormControl size="small" sx={{ minWidth: 160 }}>
        <InputLabel>Financial Year</InputLabel>
        <Select value={selectedYear} label="Financial Year" onChange={e => { setSelectedYear(e.target.value); setDateRange([{ startDate: null, endDate: null, key: 'selection' }]); }}>
          {uniqueYears.map(y => <MenuItem key={y} value={y}>{y === 'all' ? 'All Years' : `FY${String(y).slice(-2)}`}</MenuItem>)}
        </Select>
      </FormControl>
      <FormControl size="small" sx={{ minWidth: 140 }}>
        <InputLabel>Quarter</InputLabel>
        <Select value={selectedQuarter} label="Quarter" onChange={e => { setSelectedQuarter(e.target.value); setDateRange([{ startDate: null, endDate: null, key: 'selection' }]); }}>
          {['all', 'Q1', 'Q2', 'Q3', 'Q4'].map(q => <MenuItem key={q} value={q}>{q === 'all' ? 'All Quarters' : q}</MenuItem>)}
        </Select>
      </FormControl>
      <Badge badgeContent={dateRange[0].startDate ? 1 : 0} color="primary">
        <Button variant="outlined" size="medium" startIcon={<CalendarTodayIcon fontSize="small" />} onClick={() => setDateModal(true)}
          sx={{ textTransform: 'none', color: dateRange[0].startDate ? 'primary.main' : 'text.secondary' }}>
          {dateRange[0].startDate ? `${dateRange[0].startDate.toLocaleDateString()} — ${dateRange[0].endDate?.toLocaleDateString()}` : 'Date Range'}
        </Button>
      </Badge>
      {activeFilters > 0 && <Button size="small" startIcon={<ClearIcon />} onClick={clearAll} color="error" variant="outlined" sx={{ textTransform: 'none' }}>Clear {activeFilters} filter{activeFilters > 1 ? 's' : ''}</Button>}
      <Box sx={{ ml: isMobile ? 0 : 'auto' }}>
        <MuiTooltip title="Print or Save as PDF">
          <Button size="small" variant="outlined" startIcon={<PrintIcon />} onClick={handlePrint} sx={{ textTransform: 'none' }}>Print PDF</Button>
        </MuiTooltip>
      </Box>
    </Box>
  );

  if (loading || !hasFetchedRef.current) return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh', flexDirection: 'column', gap: 2 }}><CircularProgress size={48} thickness={3} /><Typography color="text.secondary" fontWeight={600}>Loading analytics…</Typography></Box>;
  if (error)   return <Alert severity="error" sx={{ m: 4, borderRadius: 3 }}>{error}</Alert>;

  return (
    <>
      <style>{`
        @page { size: A4 landscape; margin: 10mm; }

        @media print {
          /* 1. Hide unwanted elements */
          .no-print, aside, nav, header, [role="navigation"] {
            display: none !important;
          }

          /* 2. Break out of App layouts */
          body, html, #root {
            display: block !important;
            width: 100% !important;
            margin: 0 !important;
            padding: 0 !important;
            background: #f8f9fc !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }

          #analytics-dashboard {
            margin: 0 !important;
            padding: 0 !important;
            width: 100vw !important;
            position: absolute !important;
            left: 0 !important;
            top: 0 !important;
          }

          /* 3. FIX: Force MUI Grid items to maintain exact desktop flex-widths */
          .print-kpi { flex-basis: 20% !important; max-width: 20% !important; page-break-inside: avoid !important; }
          .print-col-8 { flex-basis: 66.666667% !important; max-width: 66.666667% !important; page-break-inside: avoid !important; break-inside: avoid !important; }
          .print-col-4 { flex-basis: 33.333333% !important; max-width: 33.333333% !important; page-break-inside: avoid !important; break-inside: avoid !important; }
          .print-col-6 { flex-basis: 50% !important; max-width: 50% !important; page-break-inside: avoid !important; break-inside: avoid !important; }

          /* 4. Keep cards intact */
          .MuiPaper-root {
            box-shadow: none !important;
            border: 1px solid #e2e8f0 !important;
            page-break-inside: avoid !important;
            break-inside: avoid !important;
            overflow: hidden !important;
          }

          /* 5. Chart printing rules */
          .chart-wrapper { width: 100% !important; overflow: hidden !important; }
          canvas { max-width: 100% !important; height: auto !important; object-fit: contain !important; }

          /* 6. Table printing rules */
          .MuiTableContainer-root { max-height: none !important; overflow: visible !important; }
          table { page-break-inside: auto; width: 100% !important; }
          tr { page-break-inside: avoid; page-break-after: auto; }
          thead { display: table-header-group; }
        }
      `}</style>

      <DashShell id="analytics-dashboard">
        {/* Header */}
        <Box sx={{ bgcolor: '#fff', borderBottom: '1px solid #e2e8f0', px: { xs: 2, md: 4 }, py: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
          {isMobile && <IconButton size="small" className="no-print" onClick={() => setFilterDrawer(true)}><Badge badgeContent={activeFilters} color="error"><MenuIcon /></Badge></IconButton>}
          <Box sx={{ flex: 1 }}>
            <Typography variant={isMobile ? 'h6' : 'h5'} fontWeight={900} letterSpacing="-0.5px">Business Performance</Typography>
            <Typography variant="caption" color="text.secondary" fontWeight={500}>
              {flatData.length} records · {selectedYear !== 'all' ? `FY${String(selectedYear).slice(-2)}` : 'All Years'} · {selectedQuarter !== 'all' ? selectedQuarter : 'All Quarters'}
              {prevFlatData && <span style={{ color: '#3b82f6', fontWeight: 700 }}> · Trend comparison active</span>}
            </Typography>
          </Box>
        </Box>

        {/* Desktop sticky filters */}
        {!isMobile && <StickyFilter elevation={0} className="no-print"><FilterControls /></StickyFilter>}

        {/* Mobile drawer */}
        <Drawer anchor="left" open={filterDrawer} onClose={() => setFilterDrawer(false)} className="no-print">
          <Box sx={{ width: 300, p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography fontWeight={800} variant="h6">Filters</Typography>
              <IconButton size="small" onClick={() => setFilterDrawer(false)}><CloseIcon /></IconButton>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <FormControl size="small" fullWidth><InputLabel>Financial Year</InputLabel><Select value={selectedYear} label="Financial Year" onChange={e => { setSelectedYear(e.target.value); setDateRange([{ startDate: null, endDate: null, key: 'selection' }]); }}>{uniqueYears.map(y => <MenuItem key={y} value={y}>{y === 'all' ? 'All Years' : `FY${String(y).slice(-2)}`}</MenuItem>)}</Select></FormControl>
              <FormControl size="small" fullWidth><InputLabel>Quarter</InputLabel><Select value={selectedQuarter} label="Quarter" onChange={e => { setSelectedQuarter(e.target.value); setDateRange([{ startDate: null, endDate: null, key: 'selection' }]); }}>{['all', 'Q1', 'Q2', 'Q3', 'Q4'].map(q => <MenuItem key={q} value={q}>{q === 'all' ? 'All Quarters' : q}</MenuItem>)}</Select></FormControl>
              <Button variant="outlined" startIcon={<CalendarTodayIcon />} onClick={() => { setFilterDrawer(false); setDateModal(true); }} sx={{ textTransform: 'none' }}>
                {dateRange[0].startDate ? `${dateRange[0].startDate.toLocaleDateString()} → ${dateRange[0].endDate?.toLocaleDateString()}` : 'Date Range'}
              </Button>
              {activeFilters > 0 && <Button color="error" variant="outlined" startIcon={<ClearIcon />} onClick={() => { clearAll(); setFilterDrawer(false); }}>Clear all filters</Button>}
              <Divider />
              <Button variant="outlined" startIcon={<PrintIcon />} onClick={handlePrint} sx={{ textTransform: 'none' }}>Print / PDF</Button>
            </Box>
          </Box>
        </Drawer>

        {/* Date modal */}
        <Dialog open={dateModal} onClose={() => setDateModal(false)} maxWidth="sm">
          <DialogTitle fontWeight={800}>Select Date Range</DialogTitle>
          <DialogContent sx={{ pt: 0 }}>
            <DateRange editableDateInputs onChange={item => { setDateRange([item.selection]); setSelectedYear('all'); setSelectedQuarter('all'); }} moveRangeOnFirstSelection={false} ranges={dateRange} />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => { setDateRange([{ startDate: null, endDate: null, key: 'selection' }]); setDateModal(false); }}>Clear</Button>
            <Button variant="contained" onClick={() => setDateModal(false)}>Apply</Button>
          </DialogActions>
        </Dialog>

        {/* Tabs */}
        <Box sx={{ bgcolor: '#fff', borderBottom: '1px solid #e2e8f0', px: { xs: 1, md: 3 } }} className="no-print">
          <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} variant="scrollable" scrollButtons="auto">
            {tabs.map((t, i) => <StyledTab key={i} label={isMobile ? '' : t.label} icon={t.icon} iconPosition={isMobile ? 'top' : 'start'} />)}
          </Tabs>
        </Box>

        <SectionWrap>
          {/* ── Overview ── */}
          {activeTab === 0 && (
            flatData.length > 0 ? (
              <>
                <KpiRow current={summary} previous={prevSummary} />
                <GainersLosers current={overviewFlatData} previous={overviewPrevFlatData} />
                <Grid container spacing={2.5}>
                  <Grid item xs={12} md={8} className="print-col-8">
                    <ChartPaper>
                      <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.3 }}>Monthly Sales vs Margin Trend</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>Sales (left axis) · Margin (right axis)</Typography>
                      <Divider sx={{ mb: 2 }} />
                      <Box sx={{ height: isMobile ? 220 : 300, position: 'relative', width: '100%', overflow: 'hidden' }} className="chart-wrapper">
                        {chartData.monthlySales.labels.length > 0 ? <Line data={chartData.monthlySales} options={dualOpts} /> : <NoData />}
                      </Box>
                    </ChartPaper>
                  </Grid>
                  <Grid item xs={12} md={4} className="print-col-4">
                    <ChartPaper>
                      <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.3 }}>Sales by Quarter</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>FY breakdown</Typography>
                      <Divider sx={{ mb: 2 }} />
                      <Box sx={{ height: isMobile ? 200 : 300, position: 'relative', width: '100%', overflow: 'hidden' }} className="chart-wrapper">
                        {chartData.salesByQuarter.labels.length > 0 ? <Bar data={chartData.salesByQuarter} options={baseOpts} /> : <NoData />}
                      </Box>
                    </ChartPaper>
                  </Grid>
                  <Grid item xs={12} md={6} className="print-col-6">
                    <DrillableChart title="Top 5 Clients by Sales" ChartComp={Bar} data={chartData.salesByClient} options={baseOpts} flatData={overviewFlatData} matchFn={(r, l) => r.clientName === l} height={chartH} />
                  </Grid>
                  <Grid item xs={12} md={6} className="print-col-6">
                    <DrillableChart title="Top 5 Clients by Margin" ChartComp={Bar} data={chartData.marginByClient} options={baseOpts} flatData={overviewFlatData} matchFn={(r, l) => r.clientName === l} height={chartH} />
                  </Grid>
                  <Grid item xs={12} md={6} className="print-col-6">
                    <DrillableChart title="Top 5 SPOC by Revenue" ChartComp={Bar} data={chartData.salesBySpoc} options={baseOpts} flatData={overviewFlatData} matchFn={(r, l) => r.salesSpoc === l} height={chartH} />
                  </Grid>
                  <Grid item xs={12} md={6} className="print-col-6">
                    <ChartPaper>
                      <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.3 }}>Sales Mix by Type</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>By cost sheet category</Typography>
                      <Divider sx={{ mb: 2 }} />
                      <Box sx={{ height: chartH, position: 'relative', width: '100%', overflow: 'hidden' }} className="chart-wrapper">
                        {chartData.salesByCsf.labels.length > 0 ? <Doughnut data={chartData.salesByCsf} options={pieOpts} /> : <NoData />}
                      </Box>
                    </ChartPaper>
                  </Grid>
                  <Grid item xs={12} md={6} className="print-col-6">
                    <DrillableChart
                    title="Top 10 Categories by Sales"
                    ChartComp={Bar} data={productChartData.salesByCategory}
                    options={baseOpts}
                    flatData={overviewProductFlatData}
                    matchFn={(r, l) => r.category === l} height={chartH}
                    />
                  </Grid>
                  <Grid item xs={12} md={6} className="print-col-6">
                    <DrillableChart title="Top 10 Brands by Sales" ChartComp={Bar} data={productChartData.salesByBrand} options={baseOpts} flatData={overviewProductFlatData} matchFn={(r, l) => r.brand === l} height={chartH} />
                  </Grid>
                </Grid>
              </>
            ) : <NoData />
          )}

          {/* ── Clients ── */}
          {activeTab === 1 && (
            <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: 3, boxShadow: '0 2px 16px rgba(0,0,0,0.06)' }}>
              <Typography variant="h6" fontWeight={800} sx={{ mb: 0.5 }}>Client Analytics</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
                Click any row to see cost sheets & QoQ breakdown · Sort by clicking column headers
              </Typography>
              {flatData.length > 0 ? <ClientPivotTable data={flatData} /> : <NoData />}
            </Paper>
          )}

          {/* ── Products ── */}
          {activeTab === 2 && (
            <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: 3, boxShadow: '0 2px 16px rgba(0,0,0,0.06)' }}>
              <Typography variant="h6" fontWeight={800} sx={{ mb: 0.5 }}>Product Analytics</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>Expand: Category → Subcategory → Brand</Typography>
              {productFlatData.length > 0 ? (
                <TreeTable data={productFlatData} buildTree={buildProductTree} searchPlaceholder="Search Category, Subcategory, Brand…" exportName="product_analytics"
                  columns={[
                    { label: 'Category / Sub / Brand' },
                    { label: 'Total Sales (INR)', key: 'totalSp',          align: 'right', render: v => fmt(v) },
                    { label: 'Total Margin (INR)',key: 'totalMargin',      align: 'right', render: v => fmt(v) },
                    { label: 'Margin %',          key: 'marginPercentage', align: 'right', render: v => <MarginChip value={v} /> },
                    { label: 'Qty',               key: 'totalQty',         align: 'right', render: v => v },
                  ]}
                />
              ) : <NoData />}
            </Paper>
          )}

          {/* ── Regional ── */}
          {activeTab === 3 && (
            <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: 3, boxShadow: '0 2px 16px rgba(0,0,0,0.06)' }}>
              <Typography variant="h6" fontWeight={800} sx={{ mb: 0.5 }}>Regional & Employee Analytics</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
                Expand: City → Employee · Click an employee row to view cost sheets & QoQ breakdown
              </Typography>
          {flatData.length > 0 ? (() => {
            const regionalGrandTotal = flatData
              .filter(d => (d.costsheetfor || '').toLowerCase() !== 'inventory')
              .reduce(
                (acc, d) => {
                acc.totalSp     += d.totalSp     || 0;
                    acc.totalPp     += d.totalPp     || 0;
                    acc.totalMargin += d.margin      || 0;
                    acc.aov         += d.totalSp     || 0;
                    acc.recordCount++;
                    return acc;
                  },
                  { totalSp: 0, totalMargin: 0, totalPp:0, aov: 0, recordCount: 0, marginPercentage: 0 }
                );
              regionalGrandTotal.marginPercentage =
                regionalGrandTotal.totalPp > 0
                  ? (regionalGrandTotal.totalMargin / regionalGrandTotal.totalPp) * 100
                  : 0;
              regionalGrandTotal.aov =
                regionalGrandTotal.recordCount > 0
                  ? regionalGrandTotal.totalPp / regionalGrandTotal.recordCount
                  : 0;
              return (
                <>
                  <TreeTable data={flatData} buildTree={buildRegionalTree} searchPlaceholder="Search city…" exportName="regional_analytics"
                    onLeafClick={(empName, empData) => setSelectedEmployee({ name: empName, records: empData._records || [] })}
                    grandTotal={regionalGrandTotal}
                    columns={[
                      { label: 'Type / City / Employee' },
                      { label: 'Total Order Value', key: 'totalSp',          align: 'right', render: v => fmt(v) },
                      { label: 'Total Margin',      key: 'totalMargin',      align: 'right', render: v => fmt(v) },
                      { label: 'Margin %',          key: 'marginPercentage', align: 'right', render: v => <MarginChip value={v} /> },
                      { label: 'Avg. Order',        key: 'aov',              align: 'right', render: v => fmt(v) },
                    ]}
                  />
                  <CostsheetDrillDialog
                    open={!!selectedEmployee}
                    onClose={() => setSelectedEmployee(null)}
                    title={selectedEmployee?.name || ''}
                    subtitle={`Employee · ${selectedEmployee?.records?.length || 0} cost sheets`}
                    records={selectedEmployee?.records || []}
                    showQoQ
                  />
                </>
              );
            })() : <NoData />}
        </Paper>
          )}

          {/* ── Vendors ── */}
          {activeTab === 4 && (
            <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: 3, boxShadow: '0 2px 16px rgba(0,0,0,0.06)' }}>
              <Typography variant="h6" fontWeight={800} sx={{ mb: 0.5 }}>Vendor Analytics</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
                Click any row to see cost sheets & QoQ breakdown · All columns sortable
              </Typography>
              {flatData.length > 0 ? <VendorTable data={flatData} /> : <NoData />}
            </Paper>
          )}

          {activeTab === 5 && (
            <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: 3, boxShadow: '0 2px 16px rgba(0,0,0,0.06)' }}>
              <Typography variant="h6" fontWeight={800} sx={{ mb: 0.5 }}>Legal Client Analytics</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
                Grouped by registered legal client · Raw names without a mapping are shown as-is and flagged "Unmapped"
              </Typography>
              {flatData.length > 0 ? <LegalClientPivotTable data={flatData} rawToLegalMap={rawToLegalMap} /> : <NoData />}
            </Paper>
          )}

          {activeTab === 6 && (
            <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: 3, boxShadow: '0 2px 16px rgba(0,0,0,0.06)' }}>
              <Typography variant="h6" fontWeight={800} sx={{ mb: 0.5 }}>Legal Vendor Analytics</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
                Grouped by registered legal vendor · Raw names without a mapping are shown as-is and flagged "Unmapped"
              </Typography>
              {flatData.length > 0 ? <LegalVendorPivotTable data={flatData} rawToLegalVendorMap={rawToLegalVendorMap} /> : <NoData />}
            </Paper>
          )}

        </SectionWrap>
      </DashShell>
    </>
  );
};

export default AnalyticsDashboard;